/**
 * Falling-bottle playfield. Economy lives in shared code; this file is gravity,
 * splash-clicks, combos, the bouncing menel/policja, and drawing the sprites.
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from "react";
import {
  BOTTLE_CLICKER,
  BOTTLE_CLICKER_SPRITE,
  butelkomatMouth,
  butelkomatRect,
  butelkomatScreen,
  canSpawnCrushed,
  canSpawnHunt,
  canSpawnPolice,
  canSpawnSack,
  crushedClickReach,
  crushedMaxOnField,
  emptyBottleClickerInputState,
  formatKaucja,
  formatKaucjaDelta,
  huntEncounterFromState,
  huntHit,
  huntTierById,
  isCrushedBottle,
  isHuntAlive,
  isPoliceAlive,
  isSackBottle,
  policeEncounterFromState,
  shiftSkupEventClocks,
  judgeTap,
  nextCrushedCooldownSec,
  nextHuntCooldownSec,
  nextPoliceCooldownSec,
  nextSackCooldownSec,
  pickSpawnedBottle,
  policeHit,
  sackClickReach,
  sackRainMultiplier,
  spawnHunt,
  spawnPolice,
  stepHunt,
  stepPolice,
  SKUP_MAX_BAGS,
  skupBagLeanDeg,
  applySkupPileTune,
  DEFAULT_SKUP_PILE_TUNE,
  layoutForHours,
  tuneFromLayout,
  type BottleClickerArtId,
  type BottleClickerBottle,
  type BottleClickerClickBind,
  type BottleReturnContext,
  type BounceEncounter,
  type DvdBounceState,
  type HuntEncounter,
  type HuntState,
  type HuntTierId,
  type HuntTierKey,
  type PoliceState,
  type SkupBag,
  type SkupBagPose,
  type SkupPileLayout,
  type SkupPileTune,
} from "@ziolo/shared";
import { BOTTLE_ART_URLS, SKUP_BAG_URL, type BottleArtKey, type BottleArtMap } from "./art.js";
import {
  addBottle,
  applyTune,
  clampSpriteX,
  createBottleWorld,
  destroyWorld,
  readPose,
  removeBottle,
  stepWorld,
} from "./bottleMatter.js";
import { playBottleSfx, unlockBottleSfx } from "./bottleSound.js";
import { DEFAULT_BOTTLE_PHYSICS, type BottlePhysicsTune } from "./bottlePhysics.js";

const INGEST_MS = 280;
const FLOAT_MS = 820;
const ACCEPT_HOLD_MS = 180;
const SCREEN_HOLD_MS = 1100;
const COMBO_WINDOW_MS = 1600;
const GROUND_REST_CRUSHED_SEC = 1.8;
const FLOOR_Y = BOTTLE_CLICKER.FIELD_HEIGHT - 10;
/** Flatten the bottle sprite onto the lot. World Y, after rotate. */
const SHADOW_SQUASH = 0.16;
const SHADOW_X_SCALE = 1.06;
const SHADOW_BLUR_PX = 8;
const SHADOW_FRINGE_PX = 5;

/**
 * Material mass. Matter gravity is the same for every body, so weight also
 * scales air drag and extra downward force: PET flutters, glass thuds.
 */
function bottleWeight(type: BottleClickerBottle): number {
  switch (type) {
    case "glass":
      return 1.85;
    case "can":
      return 1.0;
    case "crushed":
      return 0.58;
    default:
      return 0.4; // pet — the lightest
  }
}

export interface BottleFieldProps {
  spawnPerSec: number;
  autoPerSec: number;
  mixLevel: number;
  splashRadius: number;
  splashCount: number;
  returnedBottles: number;
  /** Wallet drawn on the butelkomat LCD. */
  kaucjaGrosze: number;
  getPayout: (type: BottleClickerBottle, ctx: BottleReturnContext) => number;
  onReturned: (type: BottleClickerBottle, ctx: BottleReturnContext) => void;
  /** Fired while taps look machine-made, so the page can say so out loud. */
  onMacroTap: () => void;
  /** Direct tap of the bouncing menel — give him a hot-dog. Collectors never fire this. */
  onHuntCatch: (tier: HuntTierId) => void;
  /** Menel left without a hot-dog — drop one streak tier. */
  onHuntMiss: () => void;
  /** Consecutive catches; next spawn reads huntTierForStreak. */
  huntStreak: number;
  /** Live menel restored from save. Read once on mount. */
  resumeHunt?: HuntState | null;
  /** Cooldown left, or 0 for the first-delay on a fresh mount. */
  nextHuntRemainingMs?: number;
  resumePolice?: PoliceState | null;
  nextPoliceRemainingMs?: number;
  nextSackRemainingMs?: number;
  onHuntSession?: (encounter: HuntEncounter | null, nextRemainingMs: number, paused: boolean) => void;
  onPoliceSession?: (encounter: BounceEncounter | null, nextRemainingMs: number, paused: boolean) => void;
  onSackCooldown?: (nextRemainingMs: number) => void;
  /** Direct tap of the bouncing cop. Splash and zbieracze never fire this. */
  onPoliceCatch: () => void;
  /** Direct catch of the falling sack. Splash and zbieracze never fire this. */
  onSackCatch: () => void;
  /** Shop copies thicken the rain shown on a catch. */
  sackLevel: number;
  /** While policja is active, crushed bottles spawn this many times more often. */
  crushedSpawnMult: number;
  /** Keyboard / extra mouse button that taps at the last cursor on the field. */
  clickBind: BottleClickerClickBind | null;
  /** Garbage bags still on the butelkomat. Positions use the original pile, so pops leave holes. */
  skupBags: SkupBag[] | null;
  /** How many bags the pile started with, including ones already popped. */
  skupBagTotal: number;
  /** Clicks count only after the greeting is dismissed. */
  skupInteractive: boolean;
  onPopSkup: (id: number) => void;
  /** Admin live knobs for landing. Defaults stay if omitted. */
  physics?: BottlePhysicsTune;
  /** Absence in hours — picks a saved heap snapshot. */
  skupHours?: number;
  /** Admin is dragging bags; pops and gravity stay off. */
  skupEditing?: boolean;
  pileLayouts?: SkupPileLayout[];
  pileTune?: SkupPileTune;
  /** Bump to remount bag bodies after load/reset. */
  skupLayoutNonce?: number;
  children?: ReactNode;
}

export interface BottleFieldSession {
  huntEncounter: HuntEncounter | null;
  nextHuntRemainingMs: number;
  policeEncounter: BounceEncounter | null;
  nextPoliceRemainingMs: number;
  nextSackRemainingMs: number;
}

export interface BottleFieldHandle {
  snapshotPile: () => SkupBagPose[];
  selectedBagId: () => number | null;
  snapshotSession: () => BottleFieldSession | null;
}

interface FallingBottle {
  id: number;
  type: BottleClickerBottle;
  art: BottleClickerArtId;
  lucky: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  phase: "fall" | "ingest";
  ingestFromX: number;
  ingestFromY: number;
  ingestT: number;
  manual: boolean;
  awardCombo: number;
  groundAge: number;
  /** Live tilt in radians. */
  rot: number;
  /** Angular velocity, clockwise-positive (canvas). Sacks keep this; Matter bottles sync it. */
  spin: number;
  /** Material weight: PET light, glass heavy. Drives Matter density. */
  weight: number;
  /** Pseudo-3D: the floor line this bottle lands on (further back = higher up). */
  restY: number;
  /** Pseudo-3D size: far bottles are smaller, near ones larger. */
  depthScale: number;
  /** Furthest band: this bottle collides with the butelkomat. */
  hitsCabinet: boolean;
  landed: boolean;
  asleep: boolean;
}

interface FloatText {
  id: number;
  x: number;
  y: number;
  text: string;
  age: number;
  lucky: boolean;
  combo: number;
  bad: boolean;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  size: number;
  color: string;
}

interface Ripple {
  x: number;
  y: number;
  age: number;
  life: number;
}

interface BagBurstBit {
  art: BottleClickerArtId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  w: number;
  h: number;
  age: number;
  life: number;
  gold: boolean;
}

interface BagBurstSpeck {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  size: number;
  color: string;
}

interface BagBurstRing {
  x: number;
  y: number;
  age: number;
  life: number;
  gold: boolean;
}

const BAG_BURST_ART: { art: BottleClickerArtId; w: number; h: number }[] = [
  { art: "pet", w: 50, h: 100 },
  { art: "pet2", w: 50, h: 100 },
  { art: "pet3", w: 48, h: 96 },
  { art: "can", w: 48, h: 66 },
  { art: "can2", w: 48, h: 66 },
  { art: "can3", w: 46, h: 64 },
  { art: "glass", w: 46, h: 98 },
  { art: "glass2", w: 46, h: 98 },
  { art: "glass3", w: 44, h: 96 },
];
const BAG_BURST_GRAVITY = 2100;
const BAG_BURST_CAP = 96;

interface View {
  scale: number;
  ox: number;
  oy: number;
  cssW: number;
  cssH: number;
  mobile: boolean;
}

/** Show ~58% of the 1280-wide field on phones → bigger sprites in the tall play area. */
const MOBILE_VISIBLE_WIDTH_FRAC = 0.58;
const MOBILE_FIELD_MAX_PX = 680;
const MOBILE_MACHINE_PAD = 40;

/** Where the parking-lot floor starts, as a fraction down the butelkomat. */
const SKUP_FLOOR_FRAC = 0.81;

function depthBackY(): number {
  const machine = butelkomatRect();
  const floorY = machine.y + machine.height * SKUP_FLOOR_FRAC;
  return floorY + 8;
}

/** Only the furthest band (small / back of the lot) bounces off the cabinet. */
function restHitsCabinet(restY: number): boolean {
  const back = depthBackY();
  const span = Math.max(1, FLOOR_Y - back);
  return (restY - back) / span < 0.16;
}

function cabinetRoofY(): number {
  return butelkomatRect().y + 3;
}

function bottleOverCabinetX(bottle: FallingBottle): boolean {
  const machine = butelkomatRect();
  const cx = bottle.x + bottle.width / 2;
  return cx > machine.x && cx < machine.x + machine.width;
}

function bottleContactY(bottle: FallingBottle): number {
  if (bottle.hitsCabinet && bottleOverCabinetX(bottle)) {
    const cy = bottle.y + bottle.height / 2;
    const roof = cabinetRoofY();
    if (cy < roof + 90) return roof;
  }
  return bottle.restY;
}

function bottleOnCabinet(bottle: FallingBottle): boolean {
  if (!bottle.hitsCabinet || !bottleOverCabinetX(bottle)) return false;
  const roof = cabinetRoofY();
  const cy = bottle.y + bottle.height / 2;
  const speed = Math.hypot(bottle.vx, bottle.vy);
  const nearRoof = cy < roof + 80 && cy > roof - 90;
  const settled = bottle.landed || bottle.asleep || speed < 160;
  return nearRoof && settled;
}

/** Skip shadows whose center would sit on the cabinet sprite (roof and lot stay). */
function shadowOnCabinetFace(cx: number, contactY: number): boolean {
  const machine = butelkomatRect();
  const roof = cabinetRoofY();
  if (cx <= machine.x || cx >= machine.x + machine.width) return false;
  return contactY > roof + 8 && contactY < machine.y + machine.height - 4;
}

function bottleProjectedHalf(bottle: FallingBottle): { halfW: number; halfH: number } {
  const depth = bottle.depthScale;
  const hw = (bottle.width * depth) / 2;
  const hh = (bottle.height * depth) / 2;
  const c = Math.cos(bottle.rot);
  const s = Math.sin(bottle.rot);
  return {
    halfW: Math.abs(hw * c) + Math.abs(hh * s),
    halfH: Math.abs(hw * s) + Math.abs(hh * c),
  };
}

const SKUP_BAG_ASPECT = 1.05;
/** How far a fallen bag sits into the one it lands on. */
const SKUP_NEST = 0.33;
const SKUP_GRAVITY = 3400;
const SKUP_MAX_FALL = 1100;
/** How far play-mode bags start above their saved rest pose. */
const SKUP_DROP = 280;
/** Parking-lot floor for the heap — a bit above the field rim. */
const SKUP_LOT_INSET = DEFAULT_SKUP_PILE_TUNE.lotInset;

function skupPileFill(total: number): number {
  return Math.max(0, Math.min(1, (Math.max(total, 3) - 3) / (SKUP_MAX_BAGS - 3)));
}

/**
 * Nested mound on the parking lot. Rows sit on the row below, centered on
 * the butelkomat. Short trips stay small; 8 h is a heap against the machine.
 */
function skupBagBox(
  bag: SkupBag,
  total: number,
  hours = 0,
  layouts: readonly SkupPileLayout[] = [],
  tune: SkupPileTune = DEFAULT_SKUP_PILE_TUNE,
  applyLiveTune = false,
) {
  const layout = layoutForHours(layouts, hours);
  const authored = layout?.poses[bag.id - 1];
  if (authored) {
    const from = tuneFromLayout(layout);
    const scaled = applySkupPileTune(authored, from, applyLiveTune ? tune : from);
    return {
      ...scaled,
      z: 20 + Math.round(scaled.y / 6),
    };
  }
  const machine = butelkomatRect();
  const fill = skupPileFill(total);
  const rows = skupHeapRows(Math.max(total, 1), fill);
  const { row, col } = skupSlot(bag.id, rows);
  const cols = rows[row] ?? 1;
  const widest = rows.reduce((max, n) => Math.max(max, n), 1);
  const groundBottom = BOTTLE_CLICKER.FIELD_HEIGHT - tune.lotInset;
  const baseW = machine.width * tune.bagSize;
  const baseH = baseW * SKUP_BAG_ASPECT;
  const sizeJitter = 0.94 + hashUnit(bag.id * 13 + 1) * 0.12;
  const w = baseW * sizeJitter;
  const h = w * SKUP_BAG_ASPECT;
  const yStep = baseH * SKUP_NEST;
  let xStep = baseW * 0.56;
  const maxSpan = machine.width * 1.25 + (BOTTLE_CLICKER.FIELD_WIDTH * 0.7 - machine.width * 1.25) * fill;
  const span = baseW + Math.max(0, widest - 1) * xStep;
  if (span > maxSpan && widest > 1) xStep = (maxSpan - baseW) / (widest - 1);
  const rowSpan = baseW + Math.max(0, cols - 1) * xStep;
  const originX = machine.x + (machine.width - rowSpan) / 2;
  const stagger = (row % 2 === 0 ? -1 : 1) * xStep * 0.16;
  const jx = (hashUnit(bag.id * 17 + 3) - 0.5) * w * 0.18;
  const jy = (hashUnit(bag.id * 29 + 11) - 0.5) * h * 0.1;
  const x = originX + col * xStep + stagger + jx;
  const pileCx = machine.x + machine.width / 2;
  const bagCx = x + w / 2;
  const half = Math.max(maxSpan / 2, w);
  const lean = skupBagLeanDeg((bagCx - pileCx) / half);
  const jitter = (hashUnit(bag.id * 41 + 7) - 0.5) * 5 * Math.min(1, Math.abs(lean) / 8);
  const rot = lean + jitter;
  const edge = Math.abs(col - (cols - 1) / 2);
  return {
    x,
    y: groundBottom - h - row * yStep + jy,
    w,
    h,
    rot,
    z: 24 + (14 - row) * 6 + edge,
  };
}

function hashUnit(seed: number): number {
  let state = seed >>> 0;
  state = Math.imul(state ^ (state >>> 16), 0x7feb352d);
  state = Math.imul(state ^ (state >>> 15), 0x846ca68b);
  state ^= state >>> 16;
  return (state >>> 0) / 4294967296;
}

/** Bottom row first. Wide on the floor, then a shorter mound up the machine. */
function skupHeapRows(total: number, fill = 0): number[] {
  if (total <= 0) return [];
  if (total === 1) return [1];
  if (total === 2) return [2];
  if (total === 3) return [2, 1];
  const base = Math.min(9, Math.max(3, Math.round(Math.sqrt(total) * 1.25 + fill * 2)));
  const rows: number[] = [];
  let left = total;
  let width = base;
  while (left > 0) {
    let n = Math.min(width, left);
    if (left - n === 1 && n > 1) n -= 1;
    rows.push(n);
    left -= n;
    if (width > 3) width -= 1;
  }
  return rows;
}

function skupSlot(id: number, rows: number[]): { row: number; col: number } {
  let slot = Math.max(0, id - 1);
  for (let row = 0; row < rows.length; row += 1) {
    const cols = rows[row] ?? 1;
    if (slot < cols) return { row, col: slot };
    slot -= cols;
  }
  return { row: Math.max(0, rows.length - 1), col: 0 };
}

interface SkupBody {
  id: number;
  x: number;
  y: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  restRot: number;
  /** Authored / heap landing Y. Drop-in falls here; pops may land lower. */
  restY: number;
  z: number;
  /** Bag directly underneath, or 0 when this one already sits on the floor. */
  supportId: number;
  /** Spawn distance to that support, so the stack doesn't retighten by itself. */
  restGap: number;
}

function makeSkupBody(
  id: number,
  box: { x: number; y: number; w: number; h: number; rot: number; z: number },
  lift: number,
): SkupBody {
  return {
    ...box,
    id,
    y: box.y - lift,
    vy: 0,
    restRot: box.rot,
    restY: box.y,
    supportId: 0,
    restGap: 0,
  };
}

function skupVisibleGround(view: View, lotInset = SKUP_LOT_INSET): number {
  const floor = BOTTLE_CLICKER.FIELD_HEIGHT - lotInset;
  if (view.scale <= 0) return floor;
  const visible = (view.cssH - view.oy) / view.scale - lotInset;
  return Math.min(floor, visible);
}

/** Lowest a bag may fall: the parking lot, or the bag still underneath it. */
function skupRestY(body: SkupBody, others: readonly SkupBody[], ground: number): { y: number; support: SkupBody | null } {
  let y = ground - body.h;
  let support: SkupBody | null = null;
  for (const other of others) {
    if (other.id === body.id) continue;
    if (other.y <= body.y + body.h * 0.15) continue;
    if (!skupCenterOn(body, other)) continue;
    const sit = other.y - body.h * SKUP_NEST;
    if (sit < y) {
      y = sit;
      support = other;
    }
  }
  return { y, support };
}

/** True when the upper bag's middle sits on the lower bag. */
function skupCenterOn(upper: SkupBody, lower: SkupBody): boolean {
  const cx = upper.x + upper.w * 0.5;
  const pad = lower.w * 0.06;
  return cx >= lower.x + pad && cx <= lower.x + lower.w - pad;
}

/** The closest bag below this one at spawn. 0 means it already rests on the floor. */
function linkSkupSupports(bodies: SkupBody[]) {
  for (const body of bodies) {
    let best: SkupBody | null = null;
    let gap = Infinity;
    for (const other of bodies) {
      if (other.id === body.id || other.y <= body.y + 8) continue;
      if (!skupCenterOn(body, other)) continue;
      const distance = other.y - body.y;
      if (distance < gap) {
        gap = distance;
        best = other;
      }
    }
    body.supportId = best ? best.id : 0;
    body.restGap = best ? gap : 0;
  }
}

function fallSkupBody(
  body: SkupBody,
  rest: number,
  dt: number,
  support: SkupBody | null,
  stickX = false,
) {
  body.vy = Math.min(SKUP_MAX_FALL, body.vy + SKUP_GRAVITY * dt);
  body.y += body.vy * dt;
  if (stickX && support) body.x += (support.x - body.x) * Math.min(0.18, dt * 1.6);
  if (body.y >= rest) {
    body.y = rest;
    body.vy = 0;
  }
}

/**
 * Bags stay put until the one under them is collected or falls.
 * Then they drop onto the next bag, or onto the floor.
 */
function stepSkupPile(bodies: SkupBody[], dt: number, visibleGround: number) {
  const ordered = [...bodies].sort((a, b) => b.y - a.y);
  const minX = 10;
  const maxX = BOTTLE_CLICKER.FIELD_WIDTH - 10;
  for (const body of ordered) {
    const support = body.supportId ? (ordered.find((other) => other.id === body.supportId) ?? null) : null;
    if (body.supportId && !support) {
      const landing = skupRestY(body, ordered, visibleGround);
      if (landing.y > body.y + 0.6) {
        fallSkupBody(body, landing.y, dt, landing.support, true);
        if (body.y >= landing.y - 0.6) {
          body.y = landing.y;
          body.vy = 0;
          body.supportId = landing.support ? landing.support.id : 0;
          body.restGap = landing.support ? landing.support.y - body.y : 0;
          body.restY = landing.y;
        }
      } else {
        body.vy = 0;
        body.supportId = landing.support ? landing.support.id : 0;
        body.restGap = landing.support ? Math.max(0, landing.support.y - body.y) : 0;
      }
    } else if (support) {
      const sit = support.y - body.restGap;
      if (body.y < sit - 0.8) fallSkupBody(body, sit, dt, support);
      else body.vy = 0;
    } else if (body.y < body.restY - 0.8) {
      fallSkupBody(body, body.restY, dt, null);
    } else {
      body.vy = 0;
    }
    body.x = Math.max(minX, Math.min(maxX - body.w, body.x));
    body.rot = body.restRot + Math.max(-7, Math.min(7, body.vy * 0.01));
    body.z = 20 + Math.round(body.y / 6);
  }
}

const FALLBACK_FILL: Record<BottleClickerArtId, string> = {
  pet: "#66bb6a",
  pet2: "#26a69a",
  pet3: "#9ccc65",
  can: "#b0bec5",
  can2: "#ffc107",
  can3: "#e53935",
  glass: "#8d6e63",
  glass2: "#ffb74d",
  glass3: "#5d4037",
  crushed: "#c62828",
  sack: "#8d6e63",
};

/** Overlay dyed with source-atop so PNG alpha stays clear. */
const HUNT_TIER_TINT: Record<HuntTierKey, string> = {
  bronze: "rgba(176, 106, 40, 0.55)",
  silver: "rgba(186, 198, 210, 0.55)",
  gold: "rgba(255, 196, 37, 0.52)",
  crystal: "rgba(64, 220, 255, 0.5)",
};

export const BottleField = forwardRef<BottleFieldHandle, BottleFieldProps>(function BottleField(
  {
    spawnPerSec,
    autoPerSec,
    mixLevel,
    splashRadius,
    splashCount,
    returnedBottles,
    kaucjaGrosze,
    getPayout,
    onReturned,
    onMacroTap,
    onHuntCatch,
    onHuntMiss,
    huntStreak,
    resumeHunt = null,
    nextHuntRemainingMs = 0,
    resumePolice = null,
    nextPoliceRemainingMs = 0,
    nextSackRemainingMs = 0,
    onHuntSession,
    onPoliceSession,
    onSackCooldown,
    onPoliceCatch,
    onSackCatch,
    sackLevel,
    crushedSpawnMult,
    clickBind,
    skupBags,
    skupBagTotal,
    skupInteractive,
    onPopSkup,
    physics = DEFAULT_BOTTLE_PHYSICS,
    skupHours = 0,
    skupEditing = false,
    pileLayouts = [],
    pileTune = DEFAULT_SKUP_PILE_TUNE,
    skupLayoutNonce = 0,
    children,
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const huntButtonRef = useRef<HTMLButtonElement>(null);
  const policeButtonRef = useRef<HTMLButtonElement>(null);
  const sackButtonRef = useRef<HTMLButtonElement>(null);
  const sessionApiRef = useRef<{ snapshot: (now?: number) => BottleFieldSession } | null>(null);
  const spawnRef = useRef(spawnPerSec);
  const autoRef = useRef(autoPerSec);
  const mixRef = useRef(mixLevel);
  const radiusRef = useRef(splashRadius);
  const countRef = useRef(splashCount);
  const returnedRef = useRef(returnedBottles);
  const kaucjaRef = useRef(kaucjaGrosze);
  const payoutRef = useRef(getPayout);
  const onReturnedRef = useRef(onReturned);
  const onMacroRef = useRef(onMacroTap);
  const onHuntCatchRef = useRef(onHuntCatch);
  const onHuntMissRef = useRef(onHuntMiss);
  const huntStreakRef = useRef(huntStreak);
  const onHuntSessionRef = useRef(onHuntSession);
  const onPoliceSessionRef = useRef(onPoliceSession);
  const onSackCooldownRef = useRef(onSackCooldown);
  const onPoliceCatchRef = useRef(onPoliceCatch);
  const onSackCatchRef = useRef(onSackCatch);
  const sackLevelRef = useRef(sackLevel);
  const crushedSpawnMultRef = useRef(crushedSpawnMult);
  const clickBindRef = useRef(clickBind);
  const skupBagsRef = useRef(skupBags);
  const skupBagTotalRef = useRef(skupBagTotal);
  const onPopSkupRef = useRef(onPopSkup);
  const physicsRef = useRef(physics);
  const skupHoursRef = useRef(skupHours);
  const skupEditRef = useRef(skupEditing);
  const pileLayoutsRef = useRef(pileLayouts);
  const pileTuneRef = useRef(pileTune);
  const skupLayoutNonceRef = useRef(skupLayoutNonce);
  const skupSelectedRef = useRef(0);
  const skupPops = useRef<{ x: number; y: number; text: string; lucky: boolean }[]>([]);
  const skupBursts = useRef<{ x: number; y: number; gold: boolean }[]>([]);
  const burstCanvasRef = useRef<HTMLCanvasElement>(null);
  const skupBodiesRef = useRef(new Map<number, SkupBody>());
  const skupLayerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<View>({ scale: 1, ox: 0, oy: 0, cssW: 1, cssH: 1, mobile: false });
  spawnRef.current = spawnPerSec;
  autoRef.current = autoPerSec;
  mixRef.current = mixLevel;
  radiusRef.current = splashRadius;
  countRef.current = splashCount;
  returnedRef.current = returnedBottles;
  kaucjaRef.current = kaucjaGrosze;
  payoutRef.current = getPayout;
  onReturnedRef.current = onReturned;
  onMacroRef.current = onMacroTap;
  onHuntCatchRef.current = onHuntCatch;
  onHuntMissRef.current = onHuntMiss;
  huntStreakRef.current = huntStreak;
  onHuntSessionRef.current = onHuntSession;
  onPoliceSessionRef.current = onPoliceSession;
  onSackCooldownRef.current = onSackCooldown;
  onPoliceCatchRef.current = onPoliceCatch;
  onSackCatchRef.current = onSackCatch;
  sackLevelRef.current = sackLevel;
  crushedSpawnMultRef.current = crushedSpawnMult;
  clickBindRef.current = clickBind;
  skupBagsRef.current = skupBags;
  skupBagTotalRef.current = skupBagTotal;
  onPopSkupRef.current = onPopSkup;
  physicsRef.current = physics;
  skupHoursRef.current = skupHours;
  skupEditRef.current = skupEditing;
  pileLayoutsRef.current = pileLayouts;
  pileTuneRef.current = pileTune;
  skupLayoutNonceRef.current = skupLayoutNonce;

  const hasSkupBags = (skupBags?.length ?? 0) > 0;
  const pileLayoutKey = pileLayouts.map((layout) => `${layout.hours}:${layout.poses.length}`).join("|");
  useLayoutEffect(() => {
    const map = skupBodiesRef.current as Map<number, SkupBody> & { _rev?: string };
    map.clear();
    map._rev = "";
    const root = skupLayerRef.current;
    const frame = frameRef.current;
    const bags = skupBagsRef.current;
    if (!root || !bags?.length) return;
    let view = viewRef.current;
    if (view.cssW < 32 && frame) {
      const cssW = frame.clientWidth;
      const cssH = frame.clientHeight;
      if (cssW >= 32 && cssH >= 32) {
        const scale = Math.min(cssW / BOTTLE_CLICKER.FIELD_WIDTH, cssH / BOTTLE_CLICKER.FIELD_HEIGHT);
        view = {
          scale,
          ox: (cssW - BOTTLE_CLICKER.FIELD_WIDTH * scale) / 2,
          oy: (cssH - BOTTLE_CLICKER.FIELD_HEIGHT * scale) / 2,
          cssW,
          cssH,
          mobile: cssW < MOBILE_FIELD_MAX_PX,
        };
        viewRef.current = view;
      }
    }
    if (view.cssW < 32) return;
    const tune = pileTuneRef.current;
    const lift = skupEditRef.current ? 0 : SKUP_DROP;
    for (const bag of bags) {
      const box = skupBagBox(
        bag,
        skupBagTotalRef.current,
        skupHoursRef.current,
        pileLayoutsRef.current,
        tune,
        skupEditRef.current,
      );
      const body = makeSkupBody(bag.id, box, lift);
      map.set(bag.id, body);
      const button = root.querySelector(`[data-skup-id="${bag.id}"]`);
      if (!(button instanceof HTMLElement)) continue;
      button.style.left = `${view.ox + body.x * view.scale}px`;
      button.style.top = `${view.oy + body.y * view.scale}px`;
      button.style.width = `${body.w * view.scale}px`;
      button.style.height = `${body.h * view.scale}px`;
      button.style.transform = `rotate(${body.rot}deg)`;
      button.style.zIndex = String(body.z);
    }
    if (!skupEditRef.current) linkSkupSupports([...map.values()]);
  }, [pileTune.bagSize, pileTune.lotInset, skupLayoutNonce, hasSkupBags, pileLayoutKey, skupHours]);

  useImperativeHandle(ref, () => ({
    snapshotPile() {
      return [...skupBodiesRef.current.values()]
        .sort((a, b) => a.id - b.id)
        .map((body) => ({
          x: body.x,
          y: body.y,
          w: body.w,
          h: body.h,
          rot: body.restRot,
        }));
    },
    selectedBagId() {
      return skupSelectedRef.current || null;
    },
    snapshotSession() {
      return sessionApiRef.current?.snapshot() ?? null;
    },
  }));

  useEffect(() => {
    const canvasEl = canvasRef.current;
    const frameEl = frameRef.current;
    const huntMaybe = huntButtonRef.current;
    const policeMaybe = policeButtonRef.current;
    const sackMaybe = sackButtonRef.current;
    if (!canvasEl || !frameEl || !huntMaybe || !policeMaybe || !sackMaybe) return;
    const gfx = canvasEl.getContext("2d");
    if (!gfx) return;
    const canvas: HTMLCanvasElement = canvasEl;
    const frame: HTMLDivElement = frameEl;
    const huntButton: HTMLButtonElement = huntMaybe;
    const policeButton: HTMLButtonElement = policeMaybe;
    const sackButton: HTMLButtonElement = sackMaybe;
    const skupLayer = skupLayerRef.current;
    const ctx: CanvasRenderingContext2D = gfx;

    let cancelled = false;
    let raf = 0;
    let last = performance.now();
    let nextId = 1;
    let spawnAcc = 0.55;
    let autoAcc = 0;
    let acceptUntil = 0;
    let lcdPay = 0;
    let lcdUntil = 0;
    let lcdBad = false;
    let combo = 0;
    let lastManual = 0;
    let comboUntil = 0;
    let input = emptyBottleClickerInputState(performance.now());
    const mountNow = performance.now();
    let nextCrushedAt = mountNow + BOTTLE_CLICKER.CRUSHED_FIRST_DELAY_SEC * 1000;
    let nextHuntAt =
      nextHuntRemainingMs > 0
        ? mountNow + nextHuntRemainingMs
        : mountNow + BOTTLE_CLICKER.HUNT_FIRST_DELAY_SEC * 1000;
    let nextPoliceAt =
      nextPoliceRemainingMs > 0
        ? mountNow + nextPoliceRemainingMs
        : mountNow + BOTTLE_CLICKER.POLICE_FIRST_DELAY_SEC * 1000;
    let nextSackAt =
      nextSackRemainingMs > 0
        ? mountNow + nextSackRemainingMs
        : mountNow + BOTTLE_CLICKER.SACK_FIRST_DELAY_SEC * 1000;
    let hunt: HuntState | null = resumeHunt;
    let police: PoliceState | null = resumePolice;
    let eventPauseAt: number | null = skupBagsRef.current != null ? mountNow : null;
    let skupWasOn = skupBagsRef.current != null;
    let rejectFlash = 0;
    let huntFlash = 0;
    let policeFlash = 0;
    let sackFlash = 0;
    const bottles: FallingBottle[] = [];
    const matter = createBottleWorld();
    applyTune(matter, physicsRef.current);
    const floats: FloatText[] = [];
    const sparks: Spark[] = [];
    const ripples: Ripple[] = [];
    const bagBits: BagBurstBit[] = [];
    const bagSpecks: BagBurstSpeck[] = [];
    const bagRings: BagBurstRing[] = [];
    let bagBurstDirty = false;
    const view: View = { scale: 1, ox: 0, oy: 0, cssW: 1, cssH: 1, mobile: false };
    const images: BottleArtMap = {};
    const huntTintCache = new Map<HuntTierKey, HTMLCanvasElement>();
    const luckyOutlineCache = new Map<BottleArtKey, HTMLCanvasElement>();
    const shadowSpriteCache = new Map<BottleArtKey, HTMLCanvasElement>();
    const mouth = butelkomatMouth();
    const machine = butelkomatRect();
    const screen = butelkomatScreen();
    let luckyFlash = 0;
    let lastAim: { clientX: number; clientY: number; x: number; y: number } | null = null;

    function clearFallingField() {
      for (const bottle of bottles) removeBottle(matter, bottle.id);
      bottles.length = 0;
      spawnAcc = 0;
      autoAcc = 0;
    }

    function syncView() {
      const cssW = frame.clientWidth;
      const cssH = frame.clientHeight;
      if (cssW < 32 || cssH < 32) return;
      view.cssW = cssW;
      view.cssH = cssH;
      view.mobile = cssW < MOBILE_FIELD_MAX_PX;
      const fitW = cssW / BOTTLE_CLICKER.FIELD_WIDTH;
      const fitH = cssH / BOTTLE_CLICKER.FIELD_HEIGHT;
      if (!view.mobile) {
        const scale = Math.min(fitW, fitH);
        view.scale = scale;
        view.ox = (cssW - BOTTLE_CLICKER.FIELD_WIDTH * scale) / 2;
        view.oy = (cssH - BOTTLE_CLICKER.FIELD_HEIGHT * scale) / 2;
      } else {
      // Width-driven zoom for horizontal mobile play, but never crop the butelkomat.
      const zoomScale = cssW / (BOTTLE_CLICKER.FIELD_WIDTH * MOBILE_VISIBLE_WIDTH_FRAC);
      const machineFitScale = cssH / (BOTTLE_CLICKER.MACHINE_HEIGHT + MOBILE_MACHINE_PAD * 2);
      const scale = Math.min(Math.max(zoomScale, fitW), machineFitScale);
      view.scale = scale;
      view.ox = (cssW - BOTTLE_CLICKER.FIELD_WIDTH * scale) / 2;
      // Focus on the machine (slightly toward the mouth), clamp inside the field.
      const focusY = machine.y + machine.height * 0.48;
      let oy = cssH / 2 - focusY * scale;
      const topAlign = 0;
      const bottomAlign = cssH - BOTTLE_CLICKER.FIELD_HEIGHT * scale;
      if (bottomAlign < topAlign) {
        oy = Math.max(bottomAlign, Math.min(topAlign, oy));
      } else {
        oy = (cssH - BOTTLE_CLICKER.FIELD_HEIGHT * scale) / 2;
      }
      view.oy = oy;
      }
      viewRef.current = { ...view };
    }

    function playBounds() {
      const pad = 12;
      if (!view.mobile || view.scale <= 0) {
        return {
          left: pad,
          right: BOTTLE_CLICKER.FIELD_WIDTH - pad,
          top: 0,
          bottom: BOTTLE_CLICKER.FIELD_HEIGHT,
        };
      }
      const left = -view.ox / view.scale;
      const top = -view.oy / view.scale;
      const right = (view.cssW - view.ox) / view.scale;
      const bottom = (view.cssH - view.oy) / view.scale;
      return {
        left: Math.max(pad, left + pad),
        right: Math.min(BOTTLE_CLICKER.FIELD_WIDTH - pad, right - pad),
        top: Math.max(0, top),
        bottom: Math.min(BOTTLE_CLICKER.FIELD_HEIGHT, bottom),
      };
    }

    function clampDvdToPlay<T extends { x: number; y: number; vx: number; vy: number }>(
      state: T,
      size: number,
    ): T {
      if (!view.mobile) return state;
      const bounds = playBounds();
      let { x, y, vx, vy } = state;
      const maxX = Math.max(bounds.left, bounds.right - size);
      const maxY = Math.max(bounds.top, bounds.bottom - size);
      if (x < bounds.left) {
        x = bounds.left;
        vx = Math.abs(vx);
      } else if (x > maxX) {
        x = maxX;
        vx = -Math.abs(vx);
      }
      if (y < bounds.top) {
        y = bounds.top;
        vy = Math.abs(vy);
      } else if (y > maxY) {
        y = maxY;
        vy = -Math.abs(vy);
      }
      return { ...state, x, y, vx, vy };
    }

    function loadArt() {
      (Object.keys(BOTTLE_ART_URLS) as BottleArtKey[]).forEach((key) => {
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          images[key] = img;
        };
        img.onerror = () => {
          if (!cancelled) images[key] = null;
        };
        img.src = BOTTLE_ART_URLS[key];
      });
    }

    function spawnFalling(type: BottleClickerBottle, lucky: boolean, art: BottleClickerArtId = type) {
      if (skupBagsRef.current != null) return;
      if (bottles.length >= BOTTLE_CLICKER.MAX_BOTTLES) return;
      const size = BOTTLE_CLICKER_SPRITE[type];
      if (!size) return;
      const bounds = playBounds();
      const sack = isSackBottle(type);
      const weight = bottleWeight(type);
      const vx = sack ? (Math.random() - 0.5) * 90 : (Math.random() - 0.5) * 50;
      const vy = sack ? 24 + Math.random() * 28 : 40 + Math.random() * 36;
      // Pseudo-3D floor: pick a depth, land across a band instead of one line.
      const floorLineY = machine.y + machine.height * SKUP_FLOOR_FRAC;
      const depth = Math.random();
      const restY = floorLineY + 8 + (FLOOR_Y - (floorLineY + 8)) * depth;
      const hitsCabinet = restHitsCabinet(restY);
      const depthScale = sack ? 1 : (0.72 + depth * 0.42) * (0.9 + Math.random() * 0.2);
      const span = Math.max(8, bounds.right - bounds.left - size.width);
      const x = bounds.left + Math.random() * span;
      // Lighter materials (PET) tumble more; heavy glass drops with little spin.
      // The resting pose is decided on touchdown, not up front, so it lands naturally.
      const spin = sack ? 0 : ((Math.random() - 0.5) * physicsRef.current.airSpin) / weight;
      const y = -size.height - Math.random() * 36;
      const rot = sack ? 0 : (Math.random() - 0.5) * Math.PI;
      const bottle: FallingBottle = {
        id: nextId,
        type,
        art,
        lucky,
        x,
        y,
        vx,
        vy,
        width: size.width,
        height: size.height,
        phase: "fall",
        ingestFromX: x,
        ingestFromY: -size.height,
        ingestT: 0,
        manual: false,
        awardCombo: 1,
        groundAge: 0,
        rot,
        spin,
        weight,
        restY,
        depthScale,
        hitsCabinet,
        landed: false,
        asleep: false,
      };
      bottles.push(bottle);
      if (!sack) {
        addBottle(matter, {
          id: bottle.id,
          x: bottle.x,
          y: bottle.y,
          width: bottle.width,
          height: bottle.height,
          depthScale: bottle.depthScale,
          rot: bottle.rot,
          vx: bottle.vx,
          vy: bottle.vy,
          spin: bottle.spin,
          weight: bottle.weight,
          restY: bottle.restY,
          hitsCabinet,
        });
      }
      nextId += 1;
    }

    function spawnBottle() {
      const spawned = pickSpawnedBottle(mixRef.current);
      spawnFalling(spawned.type, spawned.lucky, spawned.art);
    }

    function crushedOnField(): number {
      let count = 0;
      for (const bottle of bottles) {
        if (bottle.phase === "fall" && isCrushedBottle(bottle.type)) count += 1;
      }
      return count;
    }

    function sackOnField(): number {
      let count = 0;
      for (const bottle of bottles) {
        if (bottle.phase === "fall" && isSackBottle(bottle.type)) count += 1;
      }
      return count;
    }

    function forgetBottle(bottle: FallingBottle) {
      removeBottle(matter, bottle.id);
      const index = bottles.indexOf(bottle);
      if (index >= 0) bottles.splice(index, 1);
    }

    function dropGroundedRegular(): boolean {
      const drop = bottles.find(
        (item) =>
          item.phase === "fall" &&
          !isCrushedBottle(item.type) &&
          !isSackBottle(item.type) &&
          item.groundAge > 0,
      );
      if (!drop) return false;
      forgetBottle(drop);
      return true;
    }

    function beginIngest(bottle: FallingBottle, now: number, manual: boolean, awardCombo: number) {
      if (isCrushedBottle(bottle.type) || isSackBottle(bottle.type)) return;
      removeBottle(matter, bottle.id);
      bottle.phase = "ingest";
      bottle.ingestFromX = bottle.x;
      bottle.ingestFromY = bottle.y;
      bottle.ingestT = 0;
      bottle.manual = manual;
      bottle.awardCombo = awardCombo;
      acceptUntil = now + ACCEPT_HOLD_MS;
    }

    function burst(x: number, y: number, lucky: boolean, bad = false) {
      const n = bad ? 14 : lucky ? 22 : 11;
      for (let i = 0; i < n; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 50 + Math.random() * (lucky || bad ? 170 : 90);
        sparks.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 50,
          age: 0,
          life: lucky || bad ? 0.85 : 0.48,
          size: lucky || bad ? 5 : 3,
          color: bad ? "#ef9a9a" : lucky ? "#ffe082" : "#a5d6a7",
        });
      }
      if (lucky) luckyFlash = 1;
      if (bad) rejectFlash = 0.7;
    }

    function placeBounceTarget(
      button: HTMLButtonElement,
      target: DvdBounceState | null,
      size: number,
      pad: number,
    ) {
      button.classList.toggle("is-on", Boolean(target));
      if (!target) return;
      // Keep a thumb-friendly floor when the playfield is short on phones.
      const minBox = view.mobile ? 52 : 40;
      const box = Math.max(minBox, (size + pad * 2) * view.scale);
      const cx = view.ox + (target.x + size / 2) * view.scale;
      const cy = view.oy + (target.y + size / 2) * view.scale;
      button.style.left = `${cx - box / 2}px`;
      button.style.top = `${cy - box / 2}px`;
      button.style.width = `${box}px`;
      button.style.height = `${box}px`;
    }

    function placeHuntTarget(target: HuntState | null) {
      if (!target) {
        huntButton.removeAttribute("data-hunt-tier");
        huntButton.setAttribute("aria-label", "Daj hot-doga menelowi");
        placeBounceTarget(huntButton, null, BOTTLE_CLICKER.HUNT_SIZE, BOTTLE_CLICKER.HUNT_HIT_PAD);
        return;
      }
      const tier = huntTierById(target.tier);
      huntButton.dataset.huntTier = tier.key;
      huntButton.setAttribute("aria-label", `Daj hot-doga — ${tier.label}`);
      placeBounceTarget(huntButton, target, tier.size, tier.hitPad);
    }

    function placePoliceTarget(target: PoliceState | null) {
      placeBounceTarget(policeButton, target, BOTTLE_CLICKER.POLICE_SIZE, BOTTLE_CLICKER.POLICE_HIT_PAD);
    }

    function liveSack(): FallingBottle | null {
      return bottles.find((item) => item.phase === "fall" && isSackBottle(item.type)) ?? null;
    }

    function placeSackTarget(bottle: FallingBottle | null) {
      sackButton.classList.toggle("is-on", Boolean(bottle));
      if (!bottle) return;
      const pad = BOTTLE_CLICKER.SACK_HIT_PAD;
      const minBox = view.mobile ? 52 : 40;
      const boxW = Math.max(minBox, (bottle.width + pad * 2) * view.scale);
      const boxH = Math.max(minBox, (bottle.height + pad * 2) * view.scale);
      const cx = view.ox + (bottle.x + bottle.width / 2) * view.scale;
      const cy = view.oy + (bottle.y + bottle.height / 2) * view.scale;
      sackButton.style.left = `${cx - boxW / 2}px`;
      sackButton.style.top = `${cy - boxH / 2}px`;
      sackButton.style.width = `${boxW}px`;
      sackButton.style.height = `${boxH}px`;
    }

    function captureSession(now: number): BottleFieldSession {
      const paused = skupBagsRef.current != null;
      const clock = eventPauseAt ?? now;
      return {
        huntEncounter: hunt ? huntEncounterFromState(hunt, clock, Date.now(), paused) : null,
        nextHuntRemainingMs: hunt ? 0 : Math.max(0, nextHuntAt - clock),
        policeEncounter: police ? policeEncounterFromState(police, clock, Date.now(), paused) : null,
        nextPoliceRemainingMs: police ? 0 : Math.max(0, nextPoliceAt - clock),
        nextSackRemainingMs: Math.max(0, nextSackAt - clock),
      };
    }

    function persistEvents(now: number) {
      const snap = captureSession(now);
      onHuntSessionRef.current?.(snap.huntEncounter, snap.nextHuntRemainingMs, snap.huntEncounter?.paused ?? skupBagsRef.current != null);
      onPoliceSessionRef.current?.(
        snap.policeEncounter,
        snap.nextPoliceRemainingMs,
        snap.policeEncounter?.paused ?? skupBagsRef.current != null,
      );
      onSackCooldownRef.current?.(snap.nextSackRemainingMs);
    }

    sessionApiRef.current = {
      snapshot(now = performance.now()) {
        return captureSession(now);
      },
    };
    persistEvents(mountNow);

    function spawnBagBurst(x: number, y: number, gold: boolean) {
      const count = gold ? 15 : 12;
      for (let i = 0; i < count; i += 1) {
        const art = BAG_BURST_ART[Math.floor(Math.random() * BAG_BURST_ART.length)];
        if (!art) continue;
        const along = i / Math.max(1, count - 1);
        const angle = -Math.PI + 0.22 + along * (Math.PI - 0.44) + (Math.random() - 0.5) * 0.46;
        const speed = (gold ? 560 : 480) + Math.random() * 460;
        const size = 0.82 + Math.random() * 0.36;
        bagBits.push({
          art: art.art,
          x: x + (Math.random() - 0.5) * 22,
          y: y + (Math.random() - 0.5) * 18,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          rot: (Math.random() - 0.5) * 0.9,
          vr: (Math.random() - 0.5) * 18,
          w: art.w * size,
          h: art.h * size,
          age: 0,
          life: 0.82 + Math.random() * 0.38,
          gold,
        });
      }
      const specks = gold ? 16 : 10;
      for (let i = 0; i < specks; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 80 + Math.random() * (gold ? 280 : 180);
        bagSpecks.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 40,
          age: 0,
          life: 0.35 + Math.random() * 0.35,
          size: gold ? 3 + Math.random() * 3.5 : 2 + Math.random() * 2.5,
          color: gold ? (Math.random() < 0.5 ? "#ffe082" : "#fff8e1") : Math.random() < 0.5 ? "#c5e1a5" : "#fff59d",
        });
      }
      bagRings.push({ x, y, age: 0, life: gold ? 0.48 : 0.38, gold });
      while (bagBits.length > BAG_BURST_CAP) bagBits.shift();
    }

    function stepBagBurst(dt: number) {
      while (skupBursts.current.length > 0) {
        const pop = skupBursts.current.shift();
        if (!pop) break;
        spawnBagBurst(pop.x, pop.y, pop.gold);
      }
      for (let i = bagBits.length - 1; i >= 0; i -= 1) {
        const bit = bagBits[i];
        if (!bit) continue;
        bit.vy += BAG_BURST_GRAVITY * dt;
        bit.vx *= 0.99;
        bit.x += bit.vx * dt;
        bit.y += bit.vy * dt;
        bit.rot += bit.vr * dt;
        bit.age += dt;
        if (bit.age >= bit.life) bagBits.splice(i, 1);
      }
      for (let i = bagSpecks.length - 1; i >= 0; i -= 1) {
        const speck = bagSpecks[i];
        if (!speck) continue;
        speck.vy += 520 * dt;
        speck.x += speck.vx * dt;
        speck.y += speck.vy * dt;
        speck.age += dt;
        if (speck.age >= speck.life) bagSpecks.splice(i, 1);
      }
      for (let i = bagRings.length - 1; i >= 0; i -= 1) {
        const ring = bagRings[i];
        if (!ring) continue;
        ring.age += dt;
        if (ring.age >= ring.life) bagRings.splice(i, 1);
      }
    }

    function paintBagBurst() {
      const overlay = burstCanvasRef.current;
      if (!overlay) return;
      const gfx = overlay.getContext("2d");
      if (!gfx) return;
      const alive = bagBits.length + bagSpecks.length + bagRings.length > 0;
      if (!alive && !bagBurstDirty) return;
      bagBurstDirty = alive;
      const dpr = window.devicePixelRatio || 1;
      const cssW = view.cssW;
      const cssH = view.cssH;
      const w = Math.max(1, Math.floor(cssW * dpr));
      const h = Math.max(1, Math.floor(cssH * dpr));
      if (overlay.width !== w || overlay.height !== h) {
        overlay.width = w;
        overlay.height = h;
      }
      gfx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gfx.clearRect(0, 0, cssW, cssH);
      if (!alive || view.scale <= 0) return;
      gfx.setTransform(dpr * view.scale, 0, 0, dpr * view.scale, view.ox * dpr, view.oy * dpr);

      for (const ring of bagRings) {
        const t = ring.age / ring.life;
        gfx.save();
        gfx.globalAlpha = Math.max(0, (1 - t) * 0.75);
        gfx.strokeStyle = ring.gold ? "rgba(255, 224, 130, 0.95)" : "rgba(220, 237, 188, 0.9)";
        gfx.lineWidth = 5 * (1 - t * 0.65);
        gfx.beginPath();
        gfx.ellipse(ring.x, ring.y, 16 + t * 110, 10 + t * 64, 0, 0, Math.PI * 2);
        gfx.stroke();
        gfx.restore();
      }

      for (const speck of bagSpecks) {
        const t = speck.age / speck.life;
        gfx.save();
        gfx.globalAlpha = Math.max(0, 1 - t);
        gfx.fillStyle = speck.color;
        gfx.beginPath();
        gfx.arc(speck.x, speck.y, speck.size * (1 - t * 0.35), 0, Math.PI * 2);
        gfx.fill();
        gfx.restore();
      }

      for (const bit of bagBits) {
        const t = bit.age / bit.life;
        const pop = Math.min(1, bit.age / 0.07);
        const fade = t < 0.58 ? 1 : Math.max(0, 1 - (t - 0.58) / 0.42);
        const alpha = pop * fade;
        const grow = 0.45 + 0.55 * pop;
        gfx.save();
        gfx.translate(bit.x, bit.y);
        gfx.rotate(bit.rot);
        gfx.globalAlpha = alpha;
        if (bit.gold) {
          const glow = gfx.createRadialGradient(0, 0, 4, 0, 0, bit.h * 0.7);
          glow.addColorStop(0, "rgba(255, 224, 130, 0.85)");
          glow.addColorStop(1, "rgba(255, 193, 7, 0)");
          gfx.fillStyle = glow;
          gfx.beginPath();
          gfx.arc(0, 0, bit.h * 0.7, 0, Math.PI * 2);
          gfx.fill();
        }
        const dw = bit.w * grow;
        const dh = bit.h * grow;
        const img = images[bit.art];
        if (img && img.naturalWidth > 0) {
          gfx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
        } else {
          gfx.fillStyle = FALLBACK_FILL[bit.art] ?? "#66bb6a";
          gfx.beginPath();
          gfx.roundRect(-dw / 2, -dh / 2, dw, dh, 8);
          gfx.fill();
        }
        gfx.restore();
      }
    }

    function placeSkupBags(dt: number) {
      const root = skupLayerRef.current;
      if (!root) return;
      while (skupPops.current.length > 0) {
        const pop = skupPops.current.shift();
        if (!pop) break;
        floats.push({
          id: nextId,
          x: pop.x,
          y: pop.y,
          text: pop.text,
          age: 0,
          lucky: pop.lucky,
          combo: 0,
          bad: false,
        });
        nextId += 1;
      }
      const bags = skupBagsRef.current ?? [];
      const map = skupBodiesRef.current;
      const fallback = pileTuneRef.current;
      const fromDomSize = Number(root.dataset.skupBagSize);
      const fromDomInset = Number(root.dataset.skupLotInset);
      const tune: SkupPileTune = {
        bagSize: Number.isFinite(fromDomSize) ? fromDomSize : fallback.bagSize,
        lotInset: Number.isFinite(fromDomInset) ? fromDomInset : fallback.lotInset,
      };
      const layoutKey = pileLayoutsRef.current
        .map((layout) => `${layout.hours}:${layout.poses.length}`)
        .join("|");
      const layoutRev = `lean-${tune.bagSize}-${tune.lotInset}-${skupLayoutNonceRef.current}-${layoutKey}`;
      if (bags.length === 0) {
        map.clear();
      } else {
        if ((map as Map<number, SkupBody> & { _rev?: string })._rev !== layoutRev) {
          map.clear();
          (map as Map<number, SkupBody> & { _rev?: string })._rev = layoutRev;
        }
        if (!skupEditRef.current && bags.length > map.size) map.clear();
        const live = new Set(bags.map((bag) => bag.id));
        for (const id of map.keys()) {
          if (!live.has(id)) map.delete(id);
        }
        let spawned = false;
        const lift = skupEditRef.current ? 0 : SKUP_DROP;
        for (const bag of bags) {
          if (map.has(bag.id)) continue;
          const box = skupBagBox(
            bag,
            skupBagTotalRef.current,
            skupHoursRef.current,
            pileLayoutsRef.current,
            tune,
            skupEditRef.current,
          );
          map.set(bag.id, makeSkupBody(bag.id, box, lift));
          spawned = true;
        }
        const bodies = [...map.values()];
        if (spawned || bodies.every((body) => body.supportId === 0)) {
          linkSkupSupports(bodies);
        }
        if (skupEditRef.current) {
          for (const body of bodies) {
            body.z = 20 + Math.round(body.y / 6);
            if (body.id === skupSelectedRef.current) body.z += 80;
          }
        } else {
          const layout = layoutForHours(pileLayoutsRef.current, skupHoursRef.current);
          const floor = tuneFromLayout(layout).lotInset;
          stepSkupPile(bodies, dt, skupVisibleGround(view, floor));
        }
      }
      const buttons = root.querySelectorAll<HTMLButtonElement>("[data-skup-id]");
      buttons.forEach((button) => {
        const body = map.get(Number(button.dataset.skupId));
        if (!body) return;
        button.style.left = `${view.ox + body.x * view.scale}px`;
        button.style.top = `${view.oy + body.y * view.scale}px`;
        button.style.width = `${body.w * view.scale}px`;
        button.style.height = `${body.h * view.scale}px`;
        button.style.transform = `rotate(${body.rot}deg)`;
        button.style.zIndex = String(body.z);
        button.classList.toggle("is-selected", Number(button.dataset.skupId) === skupSelectedRef.current);
        button.classList.toggle("is-edit", skupEditRef.current);
      });
    }

    function tryCatchHunt(event: PointerEvent) {
      if (event.button !== 0) return false;
      if (skupBagsRef.current != null) return false;
      if (!hunt) return false;
      event.preventDefault();
      if (!event.isTrusted) return true;
      const now = performance.now();
      const judged = judgeTap(input, { atMs: now, x: event.clientX, y: event.clientY });
      input = judged.state;
      if (judged.verdict === "throttled") return true;
      const byHand = judged.verdict === "human";
      if (!byHand) {
        onMacroRef.current();
        return true;
      }
      catchHunt(hunt);
      nextHuntAt = now + nextHuntCooldownSec() * 1000;
      placeHuntTarget(null);
      persistEvents(now);
      return true;
    }

    function tryCatchPolice(event: PointerEvent) {
      if (event.button !== 0) return false;
      if (skupBagsRef.current != null) return false;
      if (!police) return false;
      event.preventDefault();
      if (!event.isTrusted) return true;
      const now = performance.now();
      const judged = judgeTap(input, { atMs: now, x: event.clientX, y: event.clientY });
      input = judged.state;
      if (judged.verdict === "throttled") return true;
      const byHand = judged.verdict === "human";
      if (!byHand) {
        onMacroRef.current();
        return true;
      }
      catchPolice(police);
      nextPoliceAt = now + nextPoliceCooldownSec() * 1000;
      placePoliceTarget(null);
      persistEvents(now);
      return true;
    }

    function tryCatchSack(event: PointerEvent) {
      if (event.button !== 0) return false;
      if (skupBagsRef.current != null) return false;
      const sack = liveSack();
      if (!sack) return false;
      event.preventDefault();
      if (!event.isTrusted) return true;
      const now = performance.now();
      const judged = judgeTap(input, { atMs: now, x: event.clientX, y: event.clientY });
      input = judged.state;
      if (judged.verdict === "throttled") return true;
      const byHand = judged.verdict === "human";
      if (!byHand) {
        onMacroRef.current();
        return true;
      }
      catchSack(sack);
      nextSackAt = now + nextSackCooldownSec() * 1000;
      placeSackTarget(null);
      persistEvents(now);
      return true;
    }

    function catchHunt(target: HuntState) {
      const tier = huntTierById(target.tier);
      const size = tier.size;
      const cx = target.x + size / 2;
      const cy = target.y + size / 2;
      playBottleSfx("hunt");
      burst(cx, cy, true);
      huntFlash = 1;
      floats.push({
        id: nextId,
        x: cx,
        y: cy - 8,
        text: tier.label,
        age: 0,
        lucky: true,
        combo: 0,
        bad: false,
      });
      nextId += 1;
      onHuntCatchRef.current(target.tier);
      hunt = null;
    }

    function catchPolice(target: PoliceState) {
      const size = BOTTLE_CLICKER.POLICE_SIZE;
      const cx = target.x + size / 2;
      const cy = target.y + size / 2;
      playBottleSfx("police");
      burst(cx, cy, false, true);
      policeFlash = 1;
      floats.push({
        id: nextId,
        x: cx,
        y: cy - 8,
        text: "Zatrzymani!",
        age: 0,
        lucky: false,
        combo: 0,
        bad: true,
      });
      nextId += 1;
      onPoliceCatchRef.current();
      police = null;
      for (let i = 0; i < BOTTLE_CLICKER.POLICE_CRUSHED_BURST; i += 1) {
        if (bottles.length >= BOTTLE_CLICKER.MAX_BOTTLES) dropGroundedRegular();
        spawnFalling("crushed", false);
      }
    }

    function catchSack(bottle: FallingBottle) {
      const cx = bottle.x + bottle.width / 2;
      const cy = bottle.y + bottle.height / 2;
      const rain = sackRainMultiplier(sackLevelRef.current);
      playBottleSfx("sack");
      burst(cx, cy, true);
      sackFlash = 1;
      floats.push({
        id: nextId,
        x: cx,
        y: cy - 8,
        text: `Worek! Deszcz ×${rain.toFixed(2).replace(".", ",")}`,
        age: 0,
        lucky: true,
        combo: 0,
        bad: false,
      });
      nextId += 1;
      const index = bottles.indexOf(bottle);
      if (index >= 0) bottles.splice(index, 1);
      onSackCatchRef.current();
    }

    function rejectCrushed(bottle: FallingBottle) {
      combo = 0;
      lastManual = 0;
      comboUntil = 0;
      const ctx: BottleReturnContext = { combo: 0, manual: true, lucky: false };
      const pay = payoutRef.current("crushed", ctx);
      onReturnedRef.current("crushed", ctx);
      const cx = bottle.x + bottle.width / 2;
      const cy = bottle.y + bottle.height / 2;
      playBottleSfx("reject");
      burst(cx, cy, false, true);
      floats.push({
        id: nextId,
        x: cx,
        y: cy - 8,
        text: pay < 0 ? `Zgnieciona! ${formatKaucjaDelta(pay)}` : "Zgnieciona!",
        age: 0,
        lucky: false,
        combo: 0,
        bad: true,
      });
      nextId += 1;
      forgetBottle(bottle);
      lcdPay = pay;
      lcdBad = true;
      lcdUntil = performance.now() + SCREEN_HOLD_MS;
    }

    function completeIngest(bottle: FallingBottle) {
      const ctx: BottleReturnContext = {
        combo: bottle.awardCombo,
        manual: bottle.manual,
        lucky: bottle.lucky,
      };
      const pay = payoutRef.current(bottle.type, ctx);
      onReturnedRef.current(bottle.type, ctx);
      // Collectors stay silent. Lucky already blinged on the tap (byHand only).
      if (bottle.manual && !bottle.lucky) {
        if (bottle.type === "glass") playBottleSfx("glass");
        else if (bottle.type === "can") playBottleSfx("can");
        else playBottleSfx("catch");
      }
      if (bottle.manual && bottle.awardCombo >= 5 && bottle.awardCombo % 5 === 0) {
        playBottleSfx("combo");
      }
      burst(mouth.x, mouth.y, bottle.lucky);
      floats.push({
        id: nextId,
        x: mouth.x + (Math.random() - 0.5) * 48,
        y: mouth.y - 8,
        text: bottle.lucky ? `★ ${formatKaucjaDelta(pay)}` : formatKaucjaDelta(pay),
        age: 0,
        lucky: bottle.lucky,
        combo: bottle.awardCombo,
        bad: false,
      });
      nextId += 1;
      lcdPay = pay;
      lcdBad = false;
      lcdUntil = performance.now() + SCREEN_HOLD_MS;
    }

    function splashHits(lx: number, ly: number): FallingBottle[] {
      const radius = radiusRef.current;
      const maxHits = Math.max(1, countRef.current);
      const pad = radius;
      const scored: { bottle: FallingBottle; dist: number }[] = [];
      for (const bottle of bottles) {
        if (bottle.phase !== "fall" || isCrushedBottle(bottle.type) || isSackBottle(bottle.type)) continue;
        const cx = bottle.x + bottle.width / 2;
        const cy = bottle.y + bottle.height / 2;
        const dx = cx - lx;
        const dy = cy - ly;
        const dist = Math.hypot(dx, dy);
        const reach = Math.max(bottle.width, bottle.height) / 2 + pad;
        if (dist <= reach) scored.push({ bottle, dist });
      }
      scored.sort((a, b) => b.bottle.restY - a.bottle.restY || a.dist - b.dist);
      return scored.slice(0, maxHits).map((item) => item.bottle);
    }

    function crushedDirectHit(lx: number, ly: number): FallingBottle | null {
      let best: FallingBottle | null = null;
      let bestDist = Infinity;
      for (const bottle of bottles) {
        if (bottle.phase !== "fall" || !isCrushedBottle(bottle.type)) continue;
        const pad = BOTTLE_CLICKER.CRUSHED_HIT_PAD;
        if (
          lx < bottle.x - pad ||
          lx > bottle.x + bottle.width + pad ||
          ly < bottle.y - pad ||
          ly > bottle.y + bottle.height + pad
        ) {
          continue;
        }
        const cx = bottle.x + bottle.width / 2;
        const cy = bottle.y + bottle.height / 2;
        const dist = Math.hypot(cx - lx, cy - ly);
        if (dist > crushedClickReach(bottle.width, bottle.height)) continue;
        if (dist < bestDist) {
          bestDist = dist;
          best = bottle;
        }
      }
      return best;
    }

    function sackDirectHit(lx: number, ly: number): FallingBottle | null {
      let best: FallingBottle | null = null;
      let bestDist = Infinity;
      for (const bottle of bottles) {
        if (bottle.phase !== "fall" || !isSackBottle(bottle.type)) continue;
        const pad = BOTTLE_CLICKER.SACK_HIT_PAD;
        if (
          lx < bottle.x - pad ||
          lx > bottle.x + bottle.width + pad ||
          ly < bottle.y - pad ||
          ly > bottle.y + bottle.height + pad
        ) {
          continue;
        }
        const cx = bottle.x + bottle.width / 2;
        const cy = bottle.y + bottle.height / 2;
        const dist = Math.hypot(cx - lx, cy - ly);
        if (dist > sackClickReach(bottle.width, bottle.height)) continue;
        if (dist < bestDist) {
          bestDist = dist;
          best = bottle;
        }
      }
      return best;
    }

    function clientToLogical(clientX: number, clientY: number): { x: number; y: number } | null {
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const cssX = ((clientX - rect.left) / rect.width) * frame.clientWidth;
      const cssY = ((clientY - rect.top) / rect.height) * frame.clientHeight;
      return {
        x: (cssX - view.ox) / view.scale,
        y: (cssY - view.oy) / view.scale,
      };
    }

    let editDrag: { id: number; ox: number; oy: number } | null = null;

    function clampEditBag(body: SkupBody) {
      body.x = Math.max(8, Math.min(BOTTLE_CLICKER.FIELD_WIDTH - body.w - 8, body.x));
      body.y = Math.max(8, Math.min(BOTTLE_CLICKER.FIELD_HEIGHT - body.h - 8, body.y));
      body.restY = body.y;
    }

    function onEditPointerDown(event: PointerEvent) {
      if (!skupEditRef.current || event.button !== 0) return;
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest("[data-skup-id]");
      if (!(button instanceof HTMLElement)) return;
      const id = Number(button.dataset.skupId);
      const body = skupBodiesRef.current.get(id);
      const point = clientToLogical(event.clientX, event.clientY);
      if (!body || !point) return;
      event.preventDefault();
      event.stopPropagation();
      editDrag = { id, ox: point.x - body.x, oy: point.y - body.y };
      skupSelectedRef.current = id;
      button.classList.add("is-dragging");
      button.setPointerCapture(event.pointerId);
    }

    function onEditPointerMove(event: PointerEvent) {
      if (!editDrag) return;
      const body = skupBodiesRef.current.get(editDrag.id);
      const point = clientToLogical(event.clientX, event.clientY);
      if (!body || !point) return;
      body.x = point.x - editDrag.ox;
      body.y = point.y - editDrag.oy;
      body.vy = 0;
      clampEditBag(body);
    }

    function onEditPointerUp(event: PointerEvent) {
      if (!editDrag) return;
      const button = skupLayer?.querySelector(`[data-skup-id="${editDrag.id}"]`);
      if (button instanceof HTMLElement) {
        button.classList.remove("is-dragging");
        try {
          button.releasePointerCapture(event.pointerId);
        } catch {
          // already released
        }
      }
      editDrag = null;
    }

    function onEditWheel(event: WheelEvent) {
      if (!skupEditRef.current) return;
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest("[data-skup-id]");
      if (!(button instanceof HTMLElement)) return;
      const id = Number(button.dataset.skupId);
      const body = skupBodiesRef.current.get(id);
      if (!body) return;
      event.preventDefault();
      skupSelectedRef.current = id;
      const step = event.shiftKey ? 1 : 4;
      body.restRot += event.deltaY > 0 ? step : -step;
      body.rot = body.restRot;
    }

    function rememberAim(clientX: number, clientY: number) {
      const point = clientToLogical(clientX, clientY);
      if (!point) return;
      lastAim = { clientX, clientY, x: point.x, y: point.y };
    }

    function fieldCenterAim() {
      const rect = canvas.getBoundingClientRect();
      const clientX = rect.left + rect.width / 2;
      const clientY = rect.top + rect.height / 2;
      const point = clientToLogical(clientX, clientY) ?? {
        x: BOTTLE_CLICKER.FIELD_WIDTH / 2,
        y: BOTTLE_CLICKER.FIELD_HEIGHT / 2,
      };
      return { clientX, clientY, x: point.x, y: point.y };
    }

    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
    }

    function tapAt(clientX: number, clientY: number, lx: number, ly: number) {
      const now = performance.now();
      const judged = judgeTap(input, { atMs: now, x: clientX, y: clientY });
      input = judged.state;
      if (judged.verdict === "throttled") return;
      const byHand = judged.verdict === "human";
      if (!byHand) onMacroRef.current();

      if (skupBagsRef.current != null) return;
      ripples.push({ x: lx, y: ly, age: 0, life: 0.32 });
      if (hunt && byHand && huntHit(hunt, lx, ly)) {
        catchHunt(hunt);
        nextHuntAt = now + nextHuntCooldownSec() * 1000;
        persistEvents(now);
        return;
      }
      if (police && byHand && policeHit(police, lx, ly)) {
        catchPolice(police);
        nextPoliceAt = now + nextPoliceCooldownSec() * 1000;
        persistEvents(now);
        return;
      }
      const sack = sackDirectHit(lx, ly);
      if (sack && byHand) {
        catchSack(sack);
        nextSackAt = now + nextSackCooldownSec() * 1000;
        persistEvents(now);
        return;
      }
      const hits = splashHits(lx, ly);
      const trash = crushedDirectHit(lx, ly);
      if (trash && hits.length === 0) {
        rejectCrushed(trash);
        return;
      }
      if (hits.length === 0) return;
      if (now - lastManual > COMBO_WINDOW_MS) combo = 0;
      lastManual = now;
      comboUntil = now + COMBO_WINDOW_MS;
      for (const bottle of hits) {
        combo = byHand ? Math.min(BOTTLE_CLICKER.COMBO_CAP, combo + 1) : 0;
        beginIngest(bottle, now, byHand, byHand ? combo : 1);
        if (byHand && bottle.lucky) playBottleSfx("lucky");
      }
    }

    function onPointerMove(event: PointerEvent) {
      rememberAim(event.clientX, event.clientY);
    }

    function onPointerDown(event: PointerEvent) {
      unlockBottleSfx();
      const bind = clickBindRef.current;
      const extra =
        event.pointerType === "mouse" &&
        bind?.kind === "mouse" &&
        event.button === bind.button;
      if (event.pointerType === "mouse" && event.button !== 0 && !extra) return;
      event.preventDefault();
      if (!event.isTrusted) return;
      rememberAim(event.clientX, event.clientY);
      const point = clientToLogical(event.clientX, event.clientY);
      if (!point) return;
      tapAt(event.clientX, event.clientY, point.x, point.y);
    }

    function onKeyDown(event: KeyboardEvent) {
      const bind = clickBindRef.current;
      if (!bind || bind.kind !== "key") return;
      if (event.repeat || event.code !== bind.code) return;
      if (event.isComposing || !event.isTrusted) return;
      if (isTypingTarget(event.target)) return;
      const aim = lastAim ?? fieldCenterAim();
      event.preventDefault();
      tapAt(aim.clientX, aim.clientY, aim.x, aim.y);
    }

    function onBoundMouseDown(event: PointerEvent) {
      const bind = clickBindRef.current;
      if (!bind || bind.kind !== "mouse" || event.button !== bind.button) return;
      if (!event.isTrusted || isTypingTarget(event.target)) return;
      if (event.target === canvas) return;
      const aim = lastAim ?? fieldCenterAim();
      event.preventDefault();
      tapAt(aim.clientX, aim.clientY, aim.x, aim.y);
    }

    function onAuxClick(event: MouseEvent) {
      const bind = clickBindRef.current;
      if (bind?.kind === "mouse" && event.button === bind.button) event.preventDefault();
    }

    function drawImageOr(
      key: BottleArtKey,
      x: number,
      y: number,
      w: number,
      h: number,
      fallback: () => void,
    ) {
      const img = images[key];
      if (img && img.naturalWidth > 0) {
        ctx.drawImage(img, x, y, w, h);
        return;
      }
      fallback();
    }

    function tintedMenel(key: HuntTierKey): HTMLCanvasElement | null {
      const img = images.menel;
      if (!img || img.naturalWidth <= 0) return null;
      const cached = huntTintCache.get(key);
      if (cached) return cached;
      const sheet = document.createElement("canvas");
      sheet.width = img.naturalWidth;
      sheet.height = img.naturalHeight;
      const ink = sheet.getContext("2d");
      if (!ink) return null;
      ink.drawImage(img, 0, 0);
      ink.globalCompositeOperation = "source-atop";
      ink.fillStyle = HUNT_TIER_TINT[key];
      ink.fillRect(0, 0, sheet.width, sheet.height);
      huntTintCache.set(key, sheet);
      return sheet;
    }

    function drawBottleSprite(bottle: FallingBottle, scale: number) {
      const cx = bottle.x + bottle.width / 2;
      const cy = bottle.y + bottle.height / 2;
      const depth = isSackBottle(bottle.type) ? 1 : bottle.depthScale;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(scale * depth, scale * depth);
      if (bottle.phase === "ingest") {
        ctx.rotate(bottle.rot + bottle.ingestT * 1.2);
      } else if (isSackBottle(bottle.type)) {
        ctx.rotate(Math.sin(performance.now() / 170 + bottle.id) * 0.14);
      } else {
        ctx.rotate(bottle.rot);
      }
      if (isSackBottle(bottle.type)) {
        const glow = ctx.createRadialGradient(0, 0, 10, 0, 0, bottle.height * 0.75);
        glow.addColorStop(0, "rgba(255, 183, 77, 0.8)");
        glow.addColorStop(1, "rgba(141, 110, 99, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(0, 0, bottle.height * 0.75, 0, Math.PI * 2);
        ctx.fill();
      }
      if (bottle.lucky && !isSackBottle(bottle.type) && drawLuckyOutlined(bottle)) {
        ctx.restore();
        return;
      }
      drawImageOr(bottle.art, -bottle.width / 2, -bottle.height / 2, bottle.width, bottle.height, () => {
        ctx.fillStyle = FALLBACK_FILL[bottle.art];
        ctx.beginPath();
        ctx.roundRect(-bottle.width / 2, -bottle.height / 2, bottle.width, bottle.height, 10);
        ctx.fill();
        if (bottle.lucky) {
          ctx.strokeStyle = "rgb(255, 196, 48)";
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      });
      ctx.restore();
    }

    /** Gold rim that follows PNG alpha, not the transparent frame. */
    function luckyOutlinedSprite(key: BottleArtKey): HTMLCanvasElement | null {
      const img = images[key];
      if (!img || img.naturalWidth <= 0) return null;
      const cached = luckyOutlineCache.get(key);
      if (cached) return cached;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const rim = Math.max(3, Math.round(Math.min(w, h) * 0.04));
      const blur = Math.max(2, Math.round(Math.min(w, h) * 0.02));
      const pad = Math.ceil(rim + blur * 2) + 2;
      const expanded = document.createElement("canvas");
      expanded.width = w + pad * 2;
      expanded.height = h + pad * 2;
      const ink = expanded.getContext("2d");
      if (!ink) return null;
      const stamps = 64;
      for (let i = 0; i < stamps; i += 1) {
        const ang = (i / stamps) * Math.PI * 2;
        ink.drawImage(img, pad + Math.cos(ang) * rim, pad + Math.sin(ang) * rim);
      }
      ink.globalCompositeOperation = "source-in";
      ink.fillStyle = "rgb(255, 196, 48)";
      ink.fillRect(0, 0, expanded.width, expanded.height);
      const sheet = document.createElement("canvas");
      sheet.width = expanded.width;
      sheet.height = expanded.height;
      const out = sheet.getContext("2d");
      if (!out) return null;
      out.filter = `blur(${blur}px)`;
      out.drawImage(expanded, 0, 0);
      out.filter = "none";
      const pixels = out.getImageData(0, 0, sheet.width, sheet.height);
      const data = pixels.data;
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3] ?? 0;
        if (alpha < 36) {
          data[i + 3] = 0;
          continue;
        }
        data[i] = 255;
        data[i + 1] = 196;
        data[i + 2] = 48;
        data[i + 3] = alpha > 132 ? 255 : Math.round(((alpha - 36) / 96) * 255);
      }
      out.putImageData(pixels, 0, 0);
      out.drawImage(img, pad, pad);
      luckyOutlineCache.set(key, sheet);
      return sheet;
    }

    function drawLuckyOutlined(bottle: FallingBottle): boolean {
      const img = images[bottle.art];
      const sheet = luckyOutlinedSprite(bottle.art);
      if (!img || !sheet) return false;
      const padX = ((sheet.width - img.naturalWidth) / 2) * (bottle.width / img.naturalWidth);
      const padY = ((sheet.height - img.naturalHeight) / 2) * (bottle.height / img.naturalHeight);
      ctx.drawImage(
        sheet,
        -bottle.width / 2 - padX,
        -bottle.height / 2 - padY,
        bottle.width + padX * 2,
        bottle.height + padY * 2,
      );
      return true;
    }

    /** Black PNG stamp, blur baked once so the floor squash stays cheap. */
    function bottleShadowSprite(key: BottleArtKey): HTMLCanvasElement | null {
      const img = images[key];
      if (!img || img.naturalWidth <= 0) return null;
      const cached = shadowSpriteCache.get(key);
      if (cached) return cached;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const blur = Math.max(SHADOW_BLUR_PX, Math.round(Math.min(w, h) * 0.1));
      const sharp = document.createElement("canvas");
      sharp.width = w;
      sharp.height = h;
      const cut = sharp.getContext("2d");
      if (!cut) return null;
      cut.drawImage(img, 0, 0);
      cut.globalCompositeOperation = "source-in";
      cut.fillStyle = "#000000";
      cut.fillRect(0, 0, w, h);
      const pad = Math.ceil(blur * 3) + 2;
      const sheet = document.createElement("canvas");
      sheet.width = w + pad * 2;
      sheet.height = h + pad * 2;
      const ink = sheet.getContext("2d");
      if (!ink) return null;
      ink.filter = `blur(${blur}px)`;
      ink.drawImage(sharp, pad, pad);
      ink.filter = "none";
      shadowSpriteCache.set(key, sheet);
      return sheet;
    }

    function drawBottleShadow(bottle: FallingBottle) {
      if (isSackBottle(bottle.type)) return;
      const { halfH } = bottleProjectedHalf(bottle);
      const cx = bottle.x + bottle.width / 2;
      const cy = bottle.y + bottle.height / 2;
      const contactY = bottleContactY(bottle);
      if (shadowOnCabinetFace(cx, contactY)) return;
      const hover = Math.max(0, contactY - (cy + halfH));
      const near = bottle.landed || bottle.asleep || bottleOnCabinet(bottle) ? 1 : Math.max(0.16, 1 - hover / 280);
      const alpha = 0.26 * near;
      if (alpha < 0.03) return;
      const depth = bottle.depthScale;
      const grow = 0.72 + 0.28 * near;
      const sheet = bottleShadowSprite(bottle.art);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
      ctx.shadowBlur = SHADOW_FRINGE_PX;
      ctx.translate(cx, contactY + 1);
      ctx.scale(depth * SHADOW_X_SCALE * grow, depth * SHADOW_SQUASH * grow);
      ctx.rotate(bottle.rot);
      if (sheet) {
        const img = images[bottle.art];
        const padX =
          img && img.naturalWidth > 0
            ? ((sheet.width - img.naturalWidth) / 2) * (bottle.width / img.naturalWidth)
            : 0;
        const padY =
          img && img.naturalHeight > 0
            ? ((sheet.height - img.naturalHeight) / 2) * (bottle.height / img.naturalHeight)
            : 0;
        ctx.drawImage(
          sheet,
          -bottle.width / 2 - padX,
          -bottle.height / 2 - padY,
          bottle.width + padX * 2,
          bottle.height + padY * 2,
        );
      } else {
        ctx.fillStyle = "#000000";
        ctx.beginPath();
        ctx.ellipse(0, 0, bottle.width / 2, bottle.height / 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    function fitLcdText(text: string, maxWidth: number, maxSize: number): number {
      let size = Math.floor(maxSize);
      while (size > 7) {
        ctx.font = `800 ${size}px 'Roboto Mono', monospace`;
        if (ctx.measureText(text).width <= maxWidth) return size;
        size -= 1;
      }
      ctx.font = `800 ${size}px 'Roboto Mono', monospace`;
      return size;
    }

    function drawLcdLine(text: string, x: number, y: number, maxWidth: number, maxSize: number, color: string) {
      fitLcdText(text, maxWidth, maxSize);
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, x, y);
    }

    function drawMachineScreen(now: number) {
      const accepting = now < acceptUntil || bottles.some((item) => item.phase === "ingest");
      const showingLast = now < lcdUntil;
      let top: string;
      let bottom: string;
      let topColor = accepting ? "#e8ffb0" : "#9aff7a";
      let bottomColor = "#6fd36a";
      if (showingLast && lcdBad) {
        top = "BŁĄD";
        bottom = formatKaucjaDelta(lcdPay);
        topColor = "#ff8a80";
        bottomColor = "#ff8a80";
      } else if (showingLast) {
        top = formatKaucjaDelta(lcdPay);
        bottom = formatKaucja(kaucjaRef.current);
      } else if (returnedRef.current <= 0) {
        top = "WŁÓŻ";
        bottom = "BUTELKĘ";
        topColor = "#7cbc6a";
        bottomColor = "#7cbc6a";
      } else {
        top = `${returnedRef.current.toLocaleString("pl-PL")} szt.`;
        bottom = formatKaucja(kaucjaRef.current);
      }

      ctx.save();
      ctx.beginPath();
      ctx.roundRect(screen.x, screen.y, screen.width, screen.height, 3);
      ctx.clip();
      ctx.fillStyle = accepting ? "#16351c" : "#0c1c10";
      ctx.fillRect(screen.x, screen.y, screen.width, screen.height);
      const cx = screen.x + screen.width / 2;
      const pad = 5;
      drawLcdLine(top, cx, screen.y + screen.height * 0.34, screen.width - pad * 2, 22, topColor);
      drawLcdLine(bottom, cx, screen.y + screen.height * 0.72, screen.width - pad * 2, 15, bottomColor);
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      for (let y = screen.y; y < screen.y + screen.height; y += 3) {
        ctx.fillRect(screen.x, y, screen.width, 1);
      }
      ctx.restore();
    }

    function paint(now: number) {
      syncView();
      const cssW = view.cssW;
      const cssH = view.cssH;
      if (cssW < 32 || cssH < 32) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      const scale = view.scale;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, view.ox * dpr, view.oy * dpr);

      if (!view.mobile) {
        drawImageOr("bg", 0, 0, BOTTLE_CLICKER.FIELD_WIDTH, BOTTLE_CLICKER.FIELD_HEIGHT, () => {
          const w = BOTTLE_CLICKER.FIELD_WIDTH;
          const h = BOTTLE_CLICKER.FIELD_HEIGHT;
          const glow = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.hypot(w / 2, h / 2));
          glow.addColorStop(0, "#769474");
          glow.addColorStop(0.32, "#4e6450");
          glow.addColorStop(0.68, "#2e3830");
          glow.addColorStop(1, "#1c221e");
          ctx.fillStyle = glow;
          ctx.fillRect(0, 0, w, h);
        });
      }

      const accepting = now < acceptUntil || bottles.some((item) => item.phase === "ingest");
      ctx.fillStyle = accepting ? "#14281a" : "#0a160e";
      ctx.fillRect(screen.x - 4, screen.y - 4, screen.width + 8, screen.height + 8);

      const floorY = machine.y + machine.height * SKUP_FLOOR_FRAC;
      const ground = ctx.createLinearGradient(0, floorY, 0, BOTTLE_CLICKER.FIELD_HEIGHT);
      ground.addColorStop(0, "rgba(12, 16, 12, 0.18)");
      ground.addColorStop(1, "rgba(6, 8, 6, 0.42)");
      ctx.fillStyle = ground;
      ctx.fillRect(0, floorY, BOTTLE_CLICKER.FIELD_WIDTH, BOTTLE_CLICKER.FIELD_HEIGHT - floorY);
      ctx.beginPath();
      ctx.strokeStyle = "rgba(214, 226, 196, 0.72)";
      ctx.lineWidth = 3;
      ctx.moveTo(0, floorY);
      ctx.lineTo(BOTTLE_CLICKER.FIELD_WIDTH, floorY);
      ctx.stroke();

      // Back-to-front so nearer bottles overlap the ones landing further away.
      const falling = bottles
        .filter((item) => item.phase === "fall")
        .sort((a, b) => a.restY - b.restY || a.y + a.height / 2 - (b.y + b.height / 2));
      const rainBags = falling.filter((item) => isSackBottle(item.type));
      const drawLayer = (layer: FallingBottle[]) => {
        for (const bottle of layer) {
          if (isSackBottle(bottle.type)) continue;
          drawBottleShadow(bottle);
        }
        for (const bottle of layer) {
          if (isSackBottle(bottle.type)) continue;
          drawBottleSprite(bottle, 1);
        }
      };

      ctx.save();
      ctx.shadowColor = "rgba(0, 0, 0, 0.42)";
      ctx.shadowBlur = 32;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 16;
      drawImageOr("idle", machine.x, machine.y, machine.width, machine.height, () => {
        ctx.fillStyle = "#2e7d4f";
        ctx.beginPath();
        ctx.roundRect(machine.x, machine.y, machine.width, machine.height, 18);
        ctx.fill();
        ctx.fillStyle = "#1b1b1b";
        ctx.beginPath();
        ctx.arc(mouth.x, mouth.y, BOTTLE_CLICKER.MACHINE_MOUTH_R, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
      drawMachineScreen(now);

      drawLayer(falling);
      for (const bottle of bottles) {
        if (bottle.phase !== "ingest") continue;
        const ease = 1 - (1 - bottle.ingestT) ** 2;
        const shrink = Math.max(0, (ease - 0.7) / 0.3);
        const scaleSprite = Math.max(0.12, 1 - shrink);
        const cx = bottle.x + bottle.width / 2;
        const cy = bottle.y + bottle.height / 2;
        ctx.save();
        if (Math.hypot(cx - mouth.x, cy - mouth.y) < BOTTLE_CLICKER.MACHINE_MOUTH_R) {
          ctx.beginPath();
          ctx.arc(mouth.x, mouth.y, BOTTLE_CLICKER.MACHINE_MOUTH_R, 0, Math.PI * 2);
          ctx.clip();
        }
        drawBottleSprite(bottle, scaleSprite);
        ctx.restore();
      }

      if (hunt) {
        const tier = huntTierById(hunt.tier);
        const size = tier.size;
        const cx = hunt.x + size / 2;
        const cy = hunt.y + size / 2;
        ctx.save();
        ctx.translate(cx, cy);
        const tinted = tintedMenel(tier.key);
        if (tinted) {
          ctx.drawImage(tinted, -size / 2, -size / 2, size, size);
        } else {
          ctx.fillStyle = HUNT_TIER_TINT[tier.key];
          ctx.beginPath();
          ctx.roundRect(-size / 2, -size / 2, size, size, 16);
          ctx.fill();
          ctx.fillStyle = "#ffe082";
          ctx.font = "800 18px 'Roboto Mono', monospace";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("M", 0, 0);
        }
        ctx.restore();
      }

      if (police) {
        const size = BOTTLE_CLICKER.POLICE_SIZE;
        const cx = police.x + size / 2;
        const cy = police.y + size / 2;
        ctx.save();
        ctx.translate(cx, cy);
        const glow = ctx.createRadialGradient(0, 0, 8, 0, 0, size * 0.9);
        glow.addColorStop(0, "rgba(66, 165, 245, 0.85)");
        glow.addColorStop(1, "rgba(21, 101, 192, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.9, 0, Math.PI * 2);
        ctx.fill();
        drawImageOr("police", -size / 2, -size / 2, size, size, () => {
          ctx.fillStyle = "#1565c0";
          ctx.beginPath();
          ctx.roundRect(-size / 2, -size / 2, size, size, 16);
          ctx.fill();
          ctx.fillStyle = "#e3f2fd";
          ctx.font = "800 20px 'Roboto Mono', monospace";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("P", 0, 0);
        });
        ctx.restore();
      }

      for (const bag of rainBags) {
        drawBottleSprite(bag, 1);
      }

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (const item of floats) {
        const t = item.age / (FLOAT_MS / 1000);
        ctx.globalAlpha = Math.max(0, 1 - t);
        ctx.font =
          item.bad || item.lucky ? "800 32px 'Roboto Mono', monospace" : "700 28px 'Roboto Mono', monospace";
        ctx.fillStyle = item.bad ? "#ef9a9a" : item.lucky ? "#ffe082" : "#a5d6a7";
        ctx.fillText(item.text, item.x, item.y);
      }
      ctx.globalAlpha = 1;

      for (const spark of sparks) {
        const t = spark.age / spark.life;
        ctx.globalAlpha = Math.max(0, 1 - t);
        ctx.fillStyle = spark.color;
        ctx.beginPath();
        ctx.arc(spark.x, spark.y, spark.size * (1 - t * 0.4), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      for (const ripple of ripples) {
        const t = ripple.age / ripple.life;
        ctx.strokeStyle = `rgba(165, 214, 167, ${Math.max(0, 0.55 * (1 - t))})`;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, 12 + t * 48, 0, Math.PI * 2);
        ctx.stroke();
      }

      if (luckyFlash > 0.02) {
        const radius = Math.max(machine.width, machine.height) * 0.72;
        const glow = ctx.createRadialGradient(mouth.x, mouth.y, 12, mouth.x, mouth.y, radius);
        glow.addColorStop(0, `rgba(255, 224, 130, ${0.42 * luckyFlash})`);
        glow.addColorStop(0.4, `rgba(255, 193, 7, ${0.16 * luckyFlash})`);
        glow.addColorStop(1, "rgba(255, 213, 79, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(mouth.x, mouth.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      if (rejectFlash > 0.02) {
        const radius = Math.max(machine.width, machine.height) * 0.65;
        const glow = ctx.createRadialGradient(mouth.x, mouth.y, 10, mouth.x, mouth.y, radius);
        glow.addColorStop(0, `rgba(239, 154, 154, ${0.32 * rejectFlash})`);
        glow.addColorStop(1, "rgba(183, 28, 28, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(mouth.x, mouth.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      if (huntFlash > 0.02) {
        ctx.fillStyle = `rgba(255, 183, 77, ${0.18 * huntFlash})`;
        ctx.fillRect(0, 0, BOTTLE_CLICKER.FIELD_WIDTH, BOTTLE_CLICKER.FIELD_HEIGHT);
      }
      if (policeFlash > 0.02) {
        ctx.fillStyle = `rgba(21, 101, 192, ${0.16 * policeFlash})`;
        ctx.fillRect(0, 0, BOTTLE_CLICKER.FIELD_WIDTH, BOTTLE_CLICKER.FIELD_HEIGHT);
      }
      if (sackFlash > 0.02) {
        ctx.fillStyle = `rgba(255, 183, 77, ${0.16 * sackFlash})`;
        ctx.fillRect(0, 0, BOTTLE_CLICKER.FIELD_WIDTH, BOTTLE_CLICKER.FIELD_HEIGHT);
      }

      if (combo >= 2 && now < comboUntil) {
        ctx.font = "800 42px 'Roboto Mono', monospace";
        ctx.fillStyle = "#c8e6c9";
        ctx.fillText(`Seria ×${combo}`, BOTTLE_CLICKER.FIELD_WIDTH / 2, 52);
      }
    }

    function nearestFalling(): FallingBottle | null {
      let nearest: FallingBottle | null = null;
      let best = Infinity;
      for (const bottle of bottles) {
        if (bottle.phase !== "fall" || isCrushedBottle(bottle.type) || isSackBottle(bottle.type)) continue;
        const cx = bottle.x + bottle.width / 2;
        const cy = bottle.y + bottle.height / 2;
        const dist = (cx - mouth.x) ** 2 + (cy - mouth.y) ** 2;
        if (dist < best) {
          best = dist;
          nearest = bottle;
        }
      }
      return nearest;
    }

    function tick(now: number) {
      if (cancelled) return;
      syncView();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      const skupOn = skupBagsRef.current != null;
      if (skupOn && !skupWasOn) {
        clearFallingField();
        eventPauseAt = now;
        persistEvents(now);
      }
      if (!skupOn && skupWasOn) {
        const pauseMs = eventPauseAt != null ? now - eventPauseAt : 0;
        const shifted = shiftSkupEventClocks(
          {
            nextHuntAt,
            nextPoliceAt,
            nextSackAt,
            nextCrushedAt,
            huntBornMs: hunt?.bornMs ?? null,
            policeBornMs: police?.bornMs ?? null,
          },
          pauseMs,
        );
        nextHuntAt = shifted.nextHuntAt;
        nextPoliceAt = shifted.nextPoliceAt;
        nextSackAt = shifted.nextSackAt;
        nextCrushedAt = shifted.nextCrushedAt;
        if (hunt && shifted.huntBornMs != null) hunt = { ...hunt, bornMs: shifted.huntBornMs };
        if (police && shifted.policeBornMs != null) police = { ...police, bornMs: shifted.policeBornMs };
        eventPauseAt = null;
        persistEvents(now);
      }
      skupWasOn = skupOn;

      if (!skupOn) {
        spawnAcc += dt * spawnRef.current;
        const crushedMult = Math.max(1, crushedSpawnMultRef.current);
        if (
          now >= nextCrushedAt &&
          canSpawnCrushed(returnedRef.current, crushedOnField(), 0, crushedMaxOnField(crushedMult))
        ) {
          if (bottles.length >= BOTTLE_CLICKER.MAX_BOTTLES) dropGroundedRegular();
          if (bottles.length < BOTTLE_CLICKER.MAX_BOTTLES) {
            spawnFalling("crushed", false);
            nextCrushedAt = now + (nextCrushedCooldownSec() * 1000) / crushedMult;
          }
        }
        if (
          now >= nextSackAt &&
          canSpawnSack(returnedRef.current, sackOnField(), 0)
        ) {
          if (bottles.length >= BOTTLE_CLICKER.MAX_BOTTLES) dropGroundedRegular();
          if (bottles.length < BOTTLE_CLICKER.MAX_BOTTLES) {
            spawnFalling("sack", false);
            nextSackAt = now + nextSackCooldownSec() * 1000;
            persistEvents(now);
          }
        }
        while (spawnAcc >= 1 && bottles.length < BOTTLE_CLICKER.MAX_BOTTLES) {
          spawnAcc -= 1;
          spawnBottle();
        }

        autoAcc += dt * autoRef.current;
        while (autoAcc >= 1) {
          autoAcc -= 1;
          const nearest = nearestFalling();
          if (nearest) beginIngest(nearest, now, false, 1);
        }
      }

      if (
        !skupOn &&
        !hunt &&
        canSpawnHunt(returnedRef.current, false, Math.max(0, (nextHuntAt - now) / 1000))
      ) {
        const spawned = spawnHunt(now, Math.random, huntStreakRef.current);
        hunt = clampDvdToPlay(spawned, huntTierById(spawned.tier).size);
        persistEvents(now);
      }
      if (
        !skupOn &&
        !police &&
        canSpawnPolice(returnedRef.current, false, Math.max(0, (nextPoliceAt - now) / 1000))
      ) {
        police = clampDvdToPlay(spawnPolice(now), BOTTLE_CLICKER.POLICE_SIZE);
        persistEvents(now);
      }

      if (!skupOn && hunt) {
        hunt = clampDvdToPlay(stepHunt(hunt, dt), huntTierById(hunt.tier).size);
        if (!isHuntAlive(hunt, now)) {
          onHuntMissRef.current();
          hunt = null;
          nextHuntAt = now + nextHuntCooldownSec() * 1000;
          persistEvents(now);
        }
      }
      if (!skupOn && police) {
        police = clampDvdToPlay(stepPolice(police, dt), BOTTLE_CLICKER.POLICE_SIZE);
        if (!isPoliceAlive(police, now)) {
          police = null;
          nextPoliceAt = now + nextPoliceCooldownSec() * 1000;
          persistEvents(now);
        }
      }

      const bounds = playBounds();
      applyTune(matter, physicsRef.current);
      stepWorld(matter, dt);
      for (let i = bottles.length - 1; i >= 0; i -= 1) {
        const bottle = bottles[i];
        if (!bottle) continue;
        if (bottle.phase === "fall") {
          if (isSackBottle(bottle.type)) {
            if (skupOn) continue;
            if (!bottle.asleep) {
              bottle.vy += physicsRef.current.gravity * 1.4 * dt;
              bottle.x += bottle.vx * dt;
              bottle.y += bottle.vy * dt;
            }
            if (bottle.x < bounds.left) {
              bottle.x = bounds.left;
              bottle.vx *= -0.4;
            }
            if (bottle.x + bottle.width > bounds.right) {
              bottle.x = bounds.right - bottle.width;
              bottle.vx *= -0.4;
            }
            if (bottle.y > BOTTLE_CLICKER.FIELD_HEIGHT) bottles.splice(i, 1);
            continue;
          }
          const pose = readPose(matter, bottle.id);
          if (pose) {
            bottle.x = pose.x;
            bottle.y = pose.y;
            bottle.rot = pose.rot;
            bottle.vx = pose.vx;
            bottle.vy = pose.vy;
            bottle.spin = pose.spin;
            bottle.landed = pose.landed;
            bottle.asleep = pose.asleep;
          }
          clampSpriteX(matter, bottle.id, bounds.left, bounds.right);
          const clamped = readPose(matter, bottle.id);
          if (clamped) {
            bottle.x = clamped.x;
            bottle.vx = clamped.vx;
            bottle.y = clamped.y;
            bottle.vy = clamped.vy;
            bottle.landed = clamped.landed;
            bottle.asleep = clamped.asleep;
          }
          const { halfH } = bottleProjectedHalf(bottle);
          const cy = bottle.y + bottle.height / 2;
          const hover = bottle.restY - (cy + halfH);
          const onFloor =
            bottle.landed ||
            bottle.asleep ||
            hover < 10 ||
            bottleOnCabinet(bottle);
          if (onFloor) bottle.groundAge += dt;
          else bottle.groundAge = Math.max(0, bottle.groundAge - dt * 2);
          const rest = isCrushedBottle(bottle.type)
            ? Math.min(physicsRef.current.groundRest, GROUND_REST_CRUSHED_SEC)
            : physicsRef.current.groundRest;
          if (onFloor && bottle.groundAge >= rest) {
            removeBottle(matter, bottle.id);
            bottles.splice(i, 1);
          }
          continue;
        }

        bottle.ingestT = Math.min(1, bottle.ingestT + (dt * 1000) / INGEST_MS);
        const ease = 1 - (1 - bottle.ingestT) ** 2;
        bottle.x = bottle.ingestFromX + (mouth.x - bottle.width / 2 - bottle.ingestFromX) * ease;
        bottle.y = bottle.ingestFromY + (mouth.y - bottle.height / 2 - bottle.ingestFromY) * ease;
        if (bottle.ingestT >= 1) {
          completeIngest(bottle);
          bottles.splice(i, 1);
        }
      }

      for (let i = floats.length - 1; i >= 0; i -= 1) {
        const item = floats[i];
        if (!item) continue;
        item.age += dt;
        item.y -= 56 * dt;
        if (item.age >= FLOAT_MS / 1000) floats.splice(i, 1);
      }

      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const spark = sparks[i];
        if (!spark) continue;
        spark.age += dt;
        spark.vy += 220 * dt;
        spark.x += spark.vx * dt;
        spark.y += spark.vy * dt;
        if (spark.age >= spark.life) sparks.splice(i, 1);
      }

      for (let i = ripples.length - 1; i >= 0; i -= 1) {
        const ripple = ripples[i];
        if (!ripple) continue;
        ripple.age += dt;
        if (ripple.age >= ripple.life) ripples.splice(i, 1);
      }

      if (luckyFlash > 0) luckyFlash = Math.max(0, luckyFlash - dt * 2.4);
      if (rejectFlash > 0) rejectFlash = Math.max(0, rejectFlash - dt * 2.2);
      if (huntFlash > 0) huntFlash = Math.max(0, huntFlash - dt * 2.2);
      if (policeFlash > 0) policeFlash = Math.max(0, policeFlash - dt * 2.2);
      if (sackFlash > 0) sackFlash = Math.max(0, sackFlash - dt * 2.2);

      paint(now);
      placeHuntTarget(skupOn ? null : hunt);
      placePoliceTarget(skupOn ? null : police);
      placeSackTarget(skupOn ? null : liveSack());
      placeSkupBags(dt);
      stepBagBurst(dt);
      paintBagBurst();
      raf = requestAnimationFrame(tick);
    }

    loadArt();
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    frame.addEventListener("pointermove", onPointerMove);
    huntButton.addEventListener("pointerdown", tryCatchHunt);
    policeButton.addEventListener("pointerdown", tryCatchPolice);
    sackButton.addEventListener("pointerdown", tryCatchSack);
    skupLayer?.addEventListener("pointerdown", onEditPointerDown);
    window.addEventListener("pointermove", onEditPointerMove);
    window.addEventListener("pointerup", onEditPointerUp);
    skupLayer?.addEventListener("wheel", onEditWheel, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onBoundMouseDown);
    window.addEventListener("auxclick", onAuxClick, true);
    last = performance.now();
    tick(last);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      frame.removeEventListener("pointermove", onPointerMove);
      huntButton.removeEventListener("pointerdown", tryCatchHunt);
      policeButton.removeEventListener("pointerdown", tryCatchPolice);
      sackButton.removeEventListener("pointerdown", tryCatchSack);
      skupLayer?.removeEventListener("pointerdown", onEditPointerDown);
      window.removeEventListener("pointermove", onEditPointerMove);
      window.removeEventListener("pointerup", onEditPointerUp);
      skupLayer?.removeEventListener("wheel", onEditWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onBoundMouseDown);
      window.removeEventListener("auxclick", onAuxClick, true);
      huntButton.classList.remove("is-on");
      policeButton.classList.remove("is-on");
      sackButton.classList.remove("is-on");
      sessionApiRef.current = null;
      destroyWorld(matter);
    };
  }, []);

  return (
    <div className="bottle-clicker__field" ref={frameRef}>
      <canvas
        ref={canvasRef}
        className="bottle-clicker__canvas"
        aria-label="Butelkomat. Klikaj spadające butelki. Daj hot-doga przyjaznemu menelowi, a podniesie kaucję. Policjanta omijaj — psuje kaucję. Złap worek z butelkami, zanim przeleci przez podłogę. Zgniecionych nie klikaj — psuje serię."
      />
      <button
        ref={huntButtonRef}
        type="button"
        className="bottle-clicker__hunt-target"
        aria-label="Daj hot-doga menelowi"
      />
      <button
        ref={policeButtonRef}
        type="button"
        className="bottle-clicker__police-target"
        aria-label="Policja — nie klikaj"
      />
      <button
        ref={sackButtonRef}
        type="button"
        className="bottle-clicker__sack-target"
        aria-label="Złap worek z butelkami"
      />
      <div
        ref={skupLayerRef}
        className={`skup-field-layer${skupEditing ? " is-editing" : ""}`}
        data-skup-bag-size={pileTune.bagSize}
        data-skup-lot-inset={pileTune.lotInset}
      >
        {skupBags?.map((bag) => {
          const box = skupBagBox(bag, skupBagTotal, skupHours, pileLayouts, pileTune, skupEditing);
          return (
            <button
              key={bag.id}
              type="button"
              data-skup-id={bag.id}
              className={`skup-field-bag skup-field-bag--${bag.tier}${skupInteractive ? " is-live" : ""}${skupEditing ? " is-edit" : ""}`}
              aria-label={bag.tier === "gold" ? "Złoty worek" : "Worek"}
              onClick={(event) => {
                if (!skupInteractive || skupEditing) return;
                event.preventDefault();
                event.stopPropagation();
                const live = skupBodiesRef.current.get(bag.id);
                const originX = (live ? live.x : box.x) + (live ? live.w : box.w) / 2;
                const originY = (live ? live.y : box.y) + (live ? live.h : box.h) * 0.38;
                skupPops.current.push({
                  x: originX,
                  y: live ? live.y : box.y,
                  text: formatKaucjaDelta(bag.grosze),
                  lucky: bag.tier === "gold",
                });
                skupBursts.current.push({
                  x: originX,
                  y: originY,
                  gold: bag.tier === "gold",
                });
                unlockBottleSfx();
                playBottleSfx(bag.tier === "gold" ? "lucky" : "sack");
                onPopSkupRef.current(bag.id);
              }}
            >
              <img src={SKUP_BAG_URL} alt="" />
            </button>
          );
        })}
      </div>
      <canvas ref={burstCanvasRef} className="bottle-clicker__burst" aria-hidden="true" />
      {skupEditing && skupBags && skupBags.length > 0 ? (
        <p className="skup-field-hint">Przeciągaj worki · kółko obraca</p>
      ) : skupInteractive && skupBags && skupBags.length > 0 ? (
        <p className="skup-field-hint">Klikaj worki na butelkomacie</p>
      ) : null}
      {children}
    </div>
  );
});
